/**
 * Mock Data Service
 * 
 * Provides realistic mock data for local development and testing.
 * This service is activated when isLocalEnvironment() returns true.
 */

import { GetRatesAndRosterResponse, RatesData } from '../pages/rosterAndRates/model';
import { buildMidShiftShiftPlanOutput } from './mockMidShiftShiftPlanOutput';
import { getOverridePlans, isBaselinePlan } from '../pages/shiftPlanning/CurrentPlanSummary/v2/baselinePlan';
import { GetTargetAndWIPVolumeResponse } from '../pages/volumesAndWip/model';
import { PlanTypes, SPMSGetShiftPlanResponse } from '../types/shiftPlan';
import {
  GetWorkItemCountsResponse,
  GetWorkItemsResponse,
  WorkItemSummary,
} from '../pages/workItems/model';
import { LeaderboardData } from '../pages/leaderboard/model';
import {
  LoadWorkItemResponse,
  UpdateWorkItemStateRequest,
  UpdateWorkItemStateResponse,
  WorkItem,
} from '../pages/workItemDetail/model';
import { PlanStatus, WORK_ITEMS_PAGE_SIZE } from '../utils/constants';
import { GetShiftRosterResponse, ShiftRosterEmployee } from '../pages/stationAssignment/shiftRoster/models';
import { GetPlannedHeadcountResponse, PlannedHeadcountPath } from '../pages/stationAssignment/plannedHeadcount/models';
import {
  AssignmentAssociate,
  AssignmentDecision,
  AssignmentsMetrics,
  GetStationAssignmentsResponse,
  IsValidStationAssignmentOverrideResponse,
  ProcessPath,
  StaffingMode,
  StationAssignmentHalfData,
  PathType,
  Workstation,
  isEmptySlot,
} from '../pages/stationAssignment/assignments/models';
import {
  GetSiteLayoutConfigResponse,
  GetAvailableProcessPathsResponse,
  SaveSiteLayoutConfigResponse,
} from '../pages/settings/tabs/siteLayout/types';
import {
  CertificateFunctionMapping,
  DeleteCertificateFunctionMappingResponse,
  GetCertificateFunctionMappingsResponse,
  MappableFunction,
  UpsertCertificateFunctionMappingResponse,
} from '../pages/settings/tabs/certificateRules/types';
import {
  DryRunShiftPlanResponse,
  DryRunProcessPathEntry,
  ProcessPathFunctionType,
  OutputAdjustmentEntry,
} from '../pages/checkout/model';

// Request parameter interfaces for dynamic mock data
export interface MockRequestParams {
  warehouseId?: string;
  nodeId?: string;
  planId?: string;
  ofdDate?: string;
  [key: string]: any;
}

// Warehouse-specific data variations
const warehouseVariations: { [key: string]: any } = {
  'LAS2': {
    nodeId: 'LAS2',
    name: 'Las Vegas 2',
    baseVolume: 125000,
    associateCount: 260,
    efficiency: 0.85,
    rates: { inbound: 25.5, outbound: 45.8, support: 15.0 }
  },
  'DFW1': {
    nodeId: 'DFW1', 
    name: 'Dallas Fort Worth 1',
    baseVolume: 150000,
    associateCount: 320,
    efficiency: 0.88,
    rates: { inbound: 27.2, outbound: 48.5, support: 16.5 }
  },
  'SEA4': {
    nodeId: 'SEA4',
    name: 'Seattle 4', 
    baseVolume: 95000,
    associateCount: 210,
    efficiency: 0.82,
    rates: { inbound: 28.0, outbound: 52.0, support: 17.2 }
  },
  'default': {
    nodeId: 'LAS2',
    name: 'Default Warehouse',
    baseVolume: 125000,
    associateCount: 260, 
    efficiency: 0.85,
    rates: { inbound: 25.5, outbound: 45.8, support: 15.0 }
  }
};

// Real Production Rates Data — updated from live backend payload
export const mockRatesData: RatesData = {
  functionRates: [
    { processPath: 'C-Returns Processed', functionId: '4300016842', functionName: 'WHD RUG', functionType: 'DIRECT', rate: 8.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    { processPath: 'C-Returns Processed', functionId: '4300000047', functionName: 'Customer Returns', functionType: 'DIRECT', rate: 12.3, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    { processPath: 'C-Returns Processed', functionId: '1667843456854', functionName: 'Primary Grading', functionType: 'DIRECT', rate: 18.0, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    { processPath: 'C-Returns Processed', functionId: '4300016841', functionName: 'WHD TV', functionType: 'DIRECT', rate: 11.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    { processPath: 'C-Returns Support', functionId: '4300016758', functionName: 'C-Returns Refurbish', functionType: 'DIRECT', rate: 15.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '1599234818280', functionName: 'T-Mech Water Spider', functionType: 'DIRECT', rate: 5.5, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300018934', functionName: 'C-Return Audit', functionType: 'DIRECT', rate: 9.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300018932', functionName: 'C-Return Sort', functionType: 'DIRECT', rate: 3.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '1599234929207', functionName: 'Team_Mech_Audit', functionType: 'DIRECT', rate: 9.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300006654', functionName: 'C-Returns Prob Solve', functionType: 'DIRECT', rate: 8.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300006653', functionName: 'C-Returns Lead/PA', functionType: 'DIRECT', rate: 3.0, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '4300006651', functionName: 'C-Returns Ambassador', functionType: 'DIRECT', rate: 0.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Support', functionId: '1599234892624', functionName: 'Team_Mech_P-Solver', functionType: 'DIRECT', rate: 8.3, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    { processPath: 'C-Returns Stow', functionId: '4300006823', functionName: 'Stow C Returns', functionType: 'DIRECT', rate: 43.2, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Stow' },
    { processPath: 'AR Grading', functionId: '1667843456854', functionName: 'Primary Grading', functionType: 'DIRECT', rate: 84.03421998566397, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
    { processPath: 'AR Grading', functionId: '4300006832', functionName: 'Technical HL Grading', functionType: 'DIRECT', rate: 22.038745734116777, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
    { processPath: 'AR Grading', functionId: '4300006862', functionName: 'WD Grading - AR', functionType: 'DIRECT', rate: 14.3, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
    { processPath: 'C-Returns Clean Decant Processed', functionId: '1617400138427', functionName: 'Function', functionType: 'DIRECT', rate: 22.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Clean Decant Processed' },
    { processPath: 'AR Grading Support', functionId: '4300006896', functionName: 'WD Problem Solve', functionType: 'DIRECT', rate: 12.613712598091766, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading Support' },
    { processPath: 'AR Grading Support', functionId: '1718200607839', functionName: 'WD TL Support', functionType: 'DIRECT', rate: 0.2851684197771836, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading Support' },
    { processPath: 'AR Speciality Grading', functionId: '1617058767035', functionName: 'Spec WHD Grading 1', functionType: 'DIRECT', rate: 3.8724515809282, volume: 0.0, timeSeconds: 0.0, department: 'AR Speciality Grading' },
  ],
  processRates: [
    { processName: 'External Repairs', processType: 'PROCESS', rate: 150.287112, volume: 23.0, timeSeconds: 11444.0, department: 'Reverse Logistics' },
    { processName: 'IB Defect', processType: 'PROCESS', rate: 534.144384, volume: 2002.0, timeSeconds: 13388.0, department: 'Inbound' },
    { processName: 'Transfer Out', processType: 'PROCESS', rate: 1031.864004, volume: 22.0, timeSeconds: 0.0, department: 'DA - Building to Building Transfer' },
    { processName: 'V-Return', processType: 'PROCESS', rate: 146.26321199999998, volume: 502.0, timeSeconds: 54385.0, department: 'Reverse Logistics' },
    { processName: 'FC Summary', processType: 'PROCESS', rate: 8.464022345183578, volume: 2002.0, timeSeconds: 93349.0, department: 'FC Summary' },
    { processName: 'Stow to Prime', processType: 'PROCESS', rate: 1234.8300960000001, volume: 287.0, timeSeconds: 12202.0, department: 'Inbound' },
    { processName: 'Transfer In and Stow', processType: 'PROCESS', rate: 677.623896, volume: 2002.0, timeSeconds: 62432.0, department: 'Inbound' },
    { processName: 'Transfer Out Dock', processType: 'PROCESS', rate: 945.861588, volume: 22.0, timeSeconds: 2866.0, department: 'DA - Building to Building Transfer' },
    { processName: 'Warehouse Deals', processType: 'PROCESS', rate: 60.308496000000005, volume: 207.0, timeSeconds: 68372.0, department: 'Reverse Logistics' },
    { processName: 'C-Return', processType: 'PROCESS', rate: 407.28218400000003, volume: 4707.0, timeSeconds: 747668.0, department: 'Reverse Logistics' },
    { processName: 'RC Summary', processType: 'PROCESS', rate: 9.039636864430488, volume: 7233.0, timeSeconds: 985523.0, department: 'RC Summary' },
    { processName: 'TO Lead/PA', processType: 'PROCESS', rate: 12927.385584, volume: 22.0, timeSeconds: 5027.0, department: 'DA - Building to Building Transfer' },
    { processName: 'IB Lead/PA', processType: 'PROCESS', rate: 970.6650119999999, volume: 2002.0, timeSeconds: 5327.0, department: 'Inbound' },
    { processName: 'RSR', processType: 'PROCESS', rate: 44486.135604, volume: 7236.0, timeSeconds: 0.0, department: 'Inbound' }
  ],
  functionRatesByDepartment: {
    'C-Returns Processed': [
      { processPath: 'C-Returns Processed', functionId: '4300016842', functionName: 'WHD RUG', functionType: 'DIRECT', rate: 8.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
      { processPath: 'C-Returns Processed', functionId: '4300000047', functionName: 'Customer Returns', functionType: 'DIRECT', rate: 12.3, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
      { processPath: 'C-Returns Processed', functionId: '1667843456854', functionName: 'Primary Grading', functionType: 'DIRECT', rate: 18.0, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
      { processPath: 'C-Returns Processed', functionId: '4300016841', functionName: 'WHD TV', functionType: 'DIRECT', rate: 11.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Processed' },
    ],
    'C-Returns Support': [
      { processPath: 'C-Returns Support', functionId: '4300016758', functionName: 'C-Returns Refurbish', functionType: 'DIRECT', rate: 15.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '1599234818280', functionName: 'T-Mech Water Spider', functionType: 'DIRECT', rate: 5.5, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300018934', functionName: 'C-Return Audit', functionType: 'DIRECT', rate: 9.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300018932', functionName: 'C-Return Sort', functionType: 'DIRECT', rate: 3.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '1599234929207', functionName: 'Team_Mech_Audit', functionType: 'DIRECT', rate: 9.9, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300006654', functionName: 'C-Returns Prob Solve', functionType: 'DIRECT', rate: 8.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300006653', functionName: 'C-Returns Lead/PA', functionType: 'DIRECT', rate: 3.0, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '4300006651', functionName: 'C-Returns Ambassador', functionType: 'DIRECT', rate: 0.4, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
      { processPath: 'C-Returns Support', functionId: '1599234892624', functionName: 'Team_Mech_P-Solver', functionType: 'DIRECT', rate: 8.3, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Support' },
    ],
    'C-Returns Stow': [
      { processPath: 'C-Returns Stow', functionId: '4300006823', functionName: 'Stow C Returns', functionType: 'DIRECT', rate: 43.2, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Stow' },
    ],
    'AR Grading': [
      { processPath: 'AR Grading', functionId: '1667843456854', functionName: 'Primary Grading', functionType: 'DIRECT', rate: 84.03421998566397, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
      { processPath: 'AR Grading', functionId: '4300006832', functionName: 'Technical HL Grading', functionType: 'DIRECT', rate: 22.038745734116777, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
      { processPath: 'AR Grading', functionId: '4300006862', functionName: 'WD Grading - AR', functionType: 'DIRECT', rate: 14.3, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading' },
    ],
    'C-Returns Clean Decant Processed': [
      { processPath: 'C-Returns Clean Decant Processed', functionId: '1617400138427', functionName: 'Function', functionType: 'DIRECT', rate: 22.7, volume: 0.0, timeSeconds: 0.0, department: 'C-Returns Clean Decant Processed' },
    ],
    'AR Grading Support': [
      { processPath: 'AR Grading Support', functionId: '4300006896', functionName: 'WD Problem Solve', functionType: 'DIRECT', rate: 12.613712598091766, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading Support' },
      { processPath: 'AR Grading Support', functionId: '1718200607839', functionName: 'WD TL Support', functionType: 'DIRECT', rate: 0.2851684197771836, volume: 0.0, timeSeconds: 0.0, department: 'AR Grading Support' },
    ],
    'AR Speciality Grading': [
      { processPath: 'AR Speciality Grading', functionId: '1617058767035', functionName: 'Spec WHD Grading 1', functionType: 'DIRECT', rate: 3.8724515809282, volume: 0.0, timeSeconds: 0.0, department: 'AR Speciality Grading' },
    ],
  },
  processRatesByDepartment: {
    'FC Summary': [{ processName: 'FC Summary', processType: 'PROCESS', rate: 8.464022345183578, volume: 2002.0, timeSeconds: 93349.0, department: 'FC Summary' }],
    'Reverse Logistics': [
      { processName: 'External Repairs', processType: 'PROCESS', rate: 150.287112, volume: 23.0, timeSeconds: 11444.0, department: 'Reverse Logistics' },
      { processName: 'V-Return', processType: 'PROCESS', rate: 146.26321199999998, volume: 502.0, timeSeconds: 54385.0, department: 'Reverse Logistics' },
      { processName: 'Warehouse Deals', processType: 'PROCESS', rate: 60.308496000000005, volume: 207.0, timeSeconds: 68372.0, department: 'Reverse Logistics' },
      { processName: 'C-Return', processType: 'PROCESS', rate: 407.28218400000003, volume: 4707.0, timeSeconds: 747668.0, department: 'Reverse Logistics' }
    ],
    'DA - Building to Building Transfer': [
      { processName: 'Transfer Out', processType: 'PROCESS', rate: 1031.864004, volume: 22.0, timeSeconds: 0.0, department: 'DA - Building to Building Transfer' },
      { processName: 'Transfer Out Dock', processType: 'PROCESS', rate: 945.861588, volume: 22.0, timeSeconds: 2866.0, department: 'DA - Building to Building Transfer' },
      { processName: 'TO Lead/PA', processType: 'PROCESS', rate: 12927.385584, volume: 22.0, timeSeconds: 5027.0, department: 'DA - Building to Building Transfer' }
    ],
    'RC Summary': [{ processName: 'RC Summary', processType: 'PROCESS', rate: 9.039636864430488, volume: 7233.0, timeSeconds: 985523.0, department: 'RC Summary' }],
    'Inbound': [
      { processName: 'IB Defect', processType: 'PROCESS', rate: 534.144384, volume: 2002.0, timeSeconds: 13388.0, department: 'Inbound' },
      { processName: 'Stow to Prime', processType: 'PROCESS', rate: 1234.8300960000001, volume: 287.0, timeSeconds: 12202.0, department: 'Inbound' },
      { processName: 'Transfer In and Stow', processType: 'PROCESS', rate: 677.623896, volume: 2002.0, timeSeconds: 62432.0, department: 'Inbound' },
      { processName: 'IB Lead/PA', processType: 'PROCESS', rate: 970.6650119999999, volume: 2002.0, timeSeconds: 5327.0, department: 'Inbound' },
      { processName: 'RSR', processType: 'PROCESS', rate: 44486.135604, volume: 7236.0, timeSeconds: 0.0, department: 'Inbound' }
    ]
  }
};

// Real Production Roster and Rates Response — updated from live backend payload
export const mockRatesAndRoster: GetRatesAndRosterResponse = {
  rosterData: {
    headcount: {
      recommended: 271.0,
      vtoOpen: 0.0,
      vtoAccepted: 0.0,
      vetOpen: 2.0,
      vetAccepted: 65.0
    },
    availableLabor: {
      total:           { headcount: 407.0, hours: 2007.0 },
      vtoOpen:         { headcount: 0.0,   hours: 0.0 },
      vtoAccepted:     { headcount: 0.0,   hours: 0.0 },
      vetOpen:         { headcount: 2.0,   hours: 10.0 },    // 2 associates × ~5h VET shift
      vetAccepted:     { headcount: 65.0,  hours: 325.0 },   // 65 associates × 5h VET shift
      azReadyOpen:     { headcount: 141.0, hours: 619.0 },
      azReadyAccepted: { headcount: 71.0,  hours: 355.0 },   // 71 associates × 5h shift
      laborShare:      { headcount: 0.0,   hours: -287.0 },
      miscHours:       { headcount: 0.0,   hours: -76.0 },
    },
    totalAttendance: {
      recommended: 86.0,
      override: 0.0,
      overrideReason: '',
      overrideComment: ''
    },
    categories: [
      {
        name: 'Voluntary Extra Time',
        order: 1.0,
        associatesCount: 325.0,   // hours: VET accepted (matches availableLabor.vetAccepted.hours)
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      },
      {
        name: 'Voluntary Time Off',
        order: 2.0,
        associatesCount: 0.0,     // hours: VTO accepted (matches availableLabor.vtoAccepted.hours)
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      },
      {
        name: 'Amazon Ready',
        order: 3.0,
        associatesCount: 355.0,   // hours: Amazon Ready open + accepted combined
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      },
      {
        name: 'Labor Share',
        order: 4.0,
        associatesCount: -287.0,   // hours: negative = loaned out (matches availableLabor.laborShare.hours)
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      },
      {
        name: 'Miscellaneous Hours',
        order: 5.0,
        associatesCount: -76.0,
        rate: 0.0,
        override: 0.0,
        overrideReason: '',
        overrideComment: ''
      }
    ],
    overrideReasonList: [],
    departmentHeadcounts: {},
    departmentAttendances: {},
    departmentVolumeGoals: {
      'Customer Returns': 69746.0
    },
    functionPermissionHeadcounts: {}
  },
  ratesData: mockRatesData,
  planStatus: PlanStatus.PENDING_USER_INPUT
};

// Mock Target and WIP Volume Response - Real Production Data Structure
export const mockTargetAndWipVolume: GetTargetAndWIPVolumeResponse = {
  totalTargetVolume: 23756.0,
  primaryTargetAndWipVolume: [
    {
      label: 'CRETURN_PRIMARY_GRADING_TEAM_MECH',
      displayName: 'Primary Grading Team Mech',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_PRIMARY_GRADING',
      displayName: 'Primary Grading',
      targetVolume: 23756.0,
      wipVolume: 22716.0
    }
  ],
  secondaryTargetAndWipVolume: [
    {
      label: 'EXTERNAL_PS',
      displayName: 'ExtrnlRP PSolver',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'BOD',
      displayName: 'BOD',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'ADVANCED_DETECTION',
      displayName: 'Advanced Detection',
      targetVolume: 102.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_REFURB',
      displayName: 'C-Returns Refurbish',
      targetVolume: 885.0,
      wipVolume: 0.0
    },
    {
      label: 'WAREHOUSE_DEALS',
      displayName: 'WD Grading - AR',
      targetVolume: 3786.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_ADD_INS',
      displayName: 'C-Returns_Add-Ins',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'Team_Mech_Audit',
      displayName: 'Team_Mech_Audit',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_REPAIR_PREP',
      displayName: 'Repair Prep',
      targetVolume: 0.0,
      wipVolume: 0.0
    },
    {
      label: 'WAREHOUSE_DEALS_PS',
      displayName: 'WD Problem Solve',
      targetVolume: 197.0,
      wipVolume: 0.0
    },
    {
      label: 'CRETURN_PS_ITEM',
      displayName: 'C-Returns Prob Solve',
      targetVolume: 642.0,
      wipVolume: 0.0
    }
  ]
};

// Mock Shift Plan Data
export const mockShiftPlan: SPMSGetShiftPlanResponse = {
  planId: 'MOCK-PLAN-001',
  nodeId: 'LAS2',
  ofdDate: '2024-02-15',
  planStatus: PlanStatus.PENDING_USER_INPUT,
  createdBy: 'testuser@amazon.com',
  createdDate: '2024-02-10T10:30:00Z',
  lastModifiedBy: 'testuser@amazon.com', 
  lastModifiedDate: '2024-02-12T14:15:00Z',
  targetVolume: 125000,
  wipVolume: 8500,
  totalScheduledHours: 2496, // 312 associates * 8 hours
  efficiency: 0.85,
  utilization: 0.92,
  totalAssociates: 260,
  dayShiftAssociates: 120,
  eveningShiftAssociates: 95,
  nightShiftAssociates: 45
};

// Mock Shift Plans List
// NOTE: the V2 Version dropdown keys on `updatedAt` for both the option value and
// label (via convertUTCToLocalTime, which expects "MM/DD/YYYY HH:mm:ss"). Each plan
// must carry a distinct `updatedAt` or the versions are indistinguishable and
// selection (find by updatedAt) fails.
export const mockShiftPlansList: SPMSGetShiftPlanResponse[] = [
  {
    ...mockShiftPlan,
    // Baseline (RCC Proposed) plan for local dev — lastUpdatedBy is the system user
    // ShiftPlanScheduler (see baselinePlan.isBaselinePlan). Change this alias to a
    // non-baseline value to exercise the "No RCC Proposed Plan available" state.
    lastUpdatedBy: 'ShiftPlanScheduler',
    updatedAt: '07/22/2026 05:56:24',
    // APPROVED + planType so the report plan picker (reportPlanOptions) lists this
    // plan under "RCC auto-generated" with an SOS label in local dev.
    planStatus: PlanStatus.APPROVED,
    planType: PlanTypes.PRE_SHIFT
  },
  {
    ...mockShiftPlan,
    // Operator Override plan — a separate plan in the list, NOT authored by
    // ShiftPlanScheduler (so getOverridePlans/isBaselinePlan treats it as override).
    // Two-fetch model: its own getShiftPlanOutput carries the override curve.
    planId: 'MOCK-PLAN-002',
    lastUpdatedBy: 'operator@amazon.com',
    ofdDate: '2024-02-16',
    planStatus: PlanStatus.APPROVED,
    planType: PlanTypes.NEXT_DAY,
    targetVolume: 135000,
    wipVolume: 9200,
    updatedAt: '07/22/2026 03:41:10'
  },
  {
    ...mockShiftPlan,
    planId: 'MOCK-PLAN-003',
    lastUpdatedBy: 'operator@amazon.com',
    ofdDate: '2024-02-17',
    planStatus: PlanStatus.PENDING_APPROVAL,
    planType: PlanTypes.PRE_SHIFT,
    targetVolume: 118000,
    wipVolume: 7800,
    updatedAt: '07/22/2026 01:12:47'
  }
];

// Operator Override plan's OWN projected volume curve (two-fetch model): fetched
// separately when an override plan version is selected, then overlaid on the RCC
// recommended line. Distinct from the baseline curve so the two lines differ.
// Peak (24800) sits ABOVE the ALPS Planned Volume (23755) so the surplus labor
// story is consistent: the RCC Recommended Volume card (peak of this curve) must
// exceed ALPS when the override plan is over-staffed.
const MOCK_OPERATOR_OVERRIDE_PROJECTED = [
  { time: "07:00", recommended: 0.0 },
  { time: "08:00", recommended: 2650.0 },
  { time: "09:00", recommended: 5300.0 },
  { time: "10:00", recommended: 7950.0 },
  { time: "11:00", recommended: 9250.0 },
  { time: "12:00", recommended: 11900.0 },
  { time: "13:00", recommended: 13050.0 },
  { time: "14:00", recommended: 15850.0 },
  { time: "15:00", recommended: 17850.0 },
  { time: "16:00", recommended: 21000.0 },
  { time: "17:00", recommended: 23200.0 },
  { time: "17:30", recommended: 24800.0 }
];

// ============================================================
// Dark Mode Configuration
// Toggle this to test dark mode in local development
// ============================================================
export const MOCK_DARK_MODE_ENABLED = false; // Set to true to test dark mode

// ============================================================
// Shift plans list (getAllShiftPlans) mock
// operatorPlanCount: null returns every mock plan (baseline + 2 operator plans). A number keeps the
// RCC baseline plan plus that many operator (override) plans: 0 = none ("Start New Shift Plan"),
// 1 or more ("Rerun Shift Plan" on SOS with RCC_MID_SHIFT_REPLAN on).
// ============================================================
export const MOCK_SHIFT_PLANS_LIST: { operatorPlanCount: number | null } = { operatorPlanCount: null };

// ============================================================
// Mid-shift re-plan (RCC_MID_SHIFT_REPLAN) mock
// enabled true: getShiftPlanOutput returns the real FPS mid-shift sample (mockMidShiftShiftPlanOutput.ts) —
// elapsed hours tagged ACTUAL, remaining hours PROJECTED. Local dev resolves the flag to on, so the
// page shows the green "Actually processed" line and green hour headers.
// enabled false: today's Start-of-Shift mock (no dataKind).
// An object (not a const boolean) so tests can switch it per case.
// ============================================================
export const MOCK_MID_SHIFT_REPLAN = { enabled: false }; // Set to true to preview mid-shift actuals locally

// Configuration constants - environment-aware delays
const isTestEnvironment = () => process.env.NODE_ENV === 'test' || 
                              process.env.JEST_WORKER_ID !== undefined ||
                              (global as any).__JEST_WORKER_ID__ !== undefined;

const MOCK_API_DELAY_MS = isTestEnvironment() ? 0 : 2000; // 0ms in tests, 2s in dev
const DEFAULT_DELAY_MS = isTestEnvironment() ? 0 : 100; // 0ms in tests, 100ms in dev (reduced from 500ms)

// Global mock control - allows tests to disable mock service entirely
const isMockDisabled = () => process.env.DISABLE_MOCK_DATA_SERVICE === 'true' ||
                            (global as any).__DISABLE_MOCK_DATA_SERVICE__ === true;

console.log(`[Mock Data Service] Environment: ${process.env.NODE_ENV}, Test: ${isTestEnvironment()}, Delays: ${DEFAULT_DELAY_MS}ms`);

// Polling simulation for getShiftPlanOutput - tracks request count per planId
const shiftPlanOutputRequestCounts: { [planId: string]: number } = {};

// Deterministic rotation counter for MockDataService.getWorkItems so the
// mock cycles through overflow, end-of-list, and happy-path branches.
let workItemsCallCounter = 0;

// Mock API Response Generator
export class MockDataService {
  /**
   * Simulates API response delay for realistic testing
   * Default delay is 500ms, but can be overridden for specific testing scenarios
   */
  private static async simulateDelay(ms: number = DEFAULT_DELAY_MS): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get warehouse configuration based on warehouseId or nodeId
   */
  private static getWarehouseConfig(params?: MockRequestParams) {
    const warehouseId = params?.warehouseId || params?.nodeId;
    const config = warehouseVariations[warehouseId || 'default'] || warehouseVariations['default'];
    
    console.log(`[Mock Data] Using warehouse config for: ${warehouseId || 'default'}`, config);
    return config;
  }

  /**
   * Generate dynamic mock data based on warehouse parameters
   * Uses real production data structure with warehouse-specific variations
   */
  private static generateDynamicRatesData(warehouseConfig: any): RatesData {
    // Apply warehouse-specific multipliers to the real production rates
    const warehouseMultiplier = warehouseConfig.efficiency || 0.85;
    const volumeBase = warehouseConfig.baseVolume || 125000;
    
    // Clone and modify the real production rates data with warehouse variations
    const dynamicFunctionRates = mockRatesData.functionRates.map(rate => ({
      ...rate,
      rate: rate.rate * warehouseMultiplier,
      volume: Math.round((rate.volume || 1000) * (volumeBase / 125000)) // Scale based on warehouse size
    }));

    const dynamicProcessRates = mockRatesData.processRates.map(rate => ({
      ...rate,
      rate: rate.rate * warehouseMultiplier,
      volume: Math.round(rate.volume * (volumeBase / 125000)) // Scale based on warehouse size
    }));

    // Clone and modify department groupings
    const dynamicFunctionRatesByDepartment: { [key: string]: any[] } = {};
    Object.entries(mockRatesData.functionRatesByDepartment).forEach(([dept, rates]) => {
      dynamicFunctionRatesByDepartment[dept] = rates.map(rate => ({
        ...rate,
        rate: rate.rate * warehouseMultiplier,
        volume: Math.round((rate.volume || 1000) * (volumeBase / 125000))
      }));
    });

    const dynamicProcessRatesByDepartment: { [key: string]: any[] } = {};
    Object.entries(mockRatesData.processRatesByDepartment).forEach(([dept, rates]) => {
      dynamicProcessRatesByDepartment[dept] = rates.map(rate => ({
        ...rate,
        rate: rate.rate * warehouseMultiplier,
        volume: Math.round(rate.volume * (volumeBase / 125000))
      }));
    });
    
    return {
      functionRates: dynamicFunctionRates,
      processRates: dynamicProcessRates,
      functionRatesByDepartment: dynamicFunctionRatesByDepartment,
      processRatesByDepartment: dynamicProcessRatesByDepartment
    };
  }

  /**
   * Get mock rates and roster data with dynamic parameters
   */
  static async getRatesAndRoster(params?: MockRequestParams): Promise<GetRatesAndRosterResponse> {
    await this.simulateDelay(MOCK_API_DELAY_MS);
    
    const warehouseConfig = this.getWarehouseConfig(params);
    const dynamicRatesData = this.generateDynamicRatesData(warehouseConfig);
    
    // Deep-clone the roster so we can seed overrides for an Operator Override plan
    // without mutating the shared static mock.
    const dynamicRosterData: GetRatesAndRosterResponse = JSON.parse(JSON.stringify({
      ...mockRatesAndRoster,
      ratesData: dynamicRatesData
    }));

    // LOCAL DEV: seed category + attendance overrides for the Operator Override plan
    // (MOCK-PLAN-002, not authored by ShiftPlanScheduler) so the read-only Rates &
    // Roster tab on the published-plan page shows populated override values. The RCC
    // baseline plan keeps empty overrides. Category names match the S3 override file.
    if (this.isMockOverridePlan(params?.planId)) {
      const categoryOverrideByName: Record<string, { override: number; overrideReason: string; overrideComment: string }> = {
        'Voluntary Extra Time': { override: 200, overrideReason: 'Other', overrideComment: 'Extra VET this shift' },
        'Amazon Ready': { override: 780, overrideReason: 'Other', overrideComment: '' },
      };
      dynamicRosterData.rosterData?.categories?.forEach((category: any) => {
        const ovr = categoryOverrideByName[category.name];
        if (ovr) {
          category.override = ovr.override;
          category.overrideReason = ovr.overrideReason;
          category.overrideComment = ovr.overrideComment;
        }
      });
      if (dynamicRosterData.rosterData?.totalAttendance) {
        dynamicRosterData.rosterData.totalAttendance.override = 92;
        dynamicRosterData.rosterData.totalAttendance.overrideReason = 'Weather';
        dynamicRosterData.rosterData.totalAttendance.overrideComment = 'Storm expected';
      }
      // Seed a rate override on the first function rate so the Rates table shows one too.
      const firstFunctionRate: any = dynamicRosterData.ratesData?.functionRates?.[0];
      if (firstFunctionRate) {
        firstFunctionRate.overrideRate = Math.round((firstFunctionRate.rate ?? 0) * 1.1 * 10) / 10;
        firstFunctionRate.overrideReason = 'Learning/Veteran Curve';
        firstFunctionRate.overrideComment = 'New hires ramping';
      }
    }
    
    console.log(`[Mock Data] Returning dynamic rates and roster data for warehouse: ${warehouseConfig.name} (planId=${params?.planId}, override=${this.isMockOverridePlan(params?.planId)})`);
    return dynamicRosterData;
  }

  /**
   * LOCAL DEV helper: true when the given planId is a mock Operator Override plan
   * (present in the mock list and NOT authored by ShiftPlanScheduler). Mirrors the
   * isOverridePlan detection used by getShiftPlanOutput so override-seeding is
   * consistent across the mock APIs.
   */
  private static isMockOverridePlan(planId?: string): boolean {
    if (!planId) {
      return false;
    }
    const plan = mockShiftPlansList.find((p) => p.planId === planId);
    return !!plan
      && String(plan.lastUpdatedBy ?? '').toLowerCase().split('@')[0] !== 'shiftplanscheduler';
  }

  /**
   * Get mock target and WIP volume data with dynamic parameters
   * Uses real production data structure with dynamic scaling based on warehouse configuration
   */
  static async getTargetAndWipVolume(params?: MockRequestParams): Promise<GetTargetAndWIPVolumeResponse> {
    await this.simulateDelay(400);
    
    const warehouseConfig = this.getWarehouseConfig(params);
    const baseVolume = warehouseConfig.baseVolume;
    const scaleFactor = baseVolume / 23756.0; // Scale based on production baseline
    
    const dynamicVolumeData: GetTargetAndWIPVolumeResponse = {
      totalTargetVolume: baseVolume,
      primaryTargetAndWipVolume: [
        {
          label: 'CRETURN_PRIMARY_GRADING_TEAM_MECH',
          displayName: 'Primary Grading Team Mech',
          targetVolume: 0.0, // Keep zero values as-is
          wipVolume: 0.0
        },
        {
          label: 'CRETURN_PRIMARY_GRADING',
          displayName: 'Primary Grading',
          targetVolume: Math.round(23756.0 * scaleFactor),
          wipVolume: Math.round(22716.0 * scaleFactor)
        }
      ],
      secondaryTargetAndWipVolume: [
        {
          label: 'EXTERNAL_PS',
          displayName: 'ExtrnlRP PSolver',
          targetVolume: 0.0, // Keep zero values as-is
          wipVolume: 0.0
        },
        {
          label: 'BOD',
          displayName: 'BOD',
          targetVolume: 0.0,
          wipVolume: 0.0
        },
        {
          label: 'ADVANCED_DETECTION',
          displayName: 'Advanced Detection',
          targetVolume: Math.round(102.0 * scaleFactor),
          wipVolume: 0.0 // Keep zero values as-is
        },
        {
          label: 'CRETURN_REFURB',
          displayName: 'C-Returns Refurbish',
          targetVolume: Math.round(885.0 * scaleFactor),
          wipVolume: 0.0
        },
        {
          label: 'WAREHOUSE_DEALS',
          displayName: 'WD Grading - AR',
          targetVolume: Math.round(3786.0 * scaleFactor),
          wipVolume: 0.0
        },
        {
          label: 'CRETURN_ADD_INS',
          displayName: 'C-Returns_Add-Ins',
          targetVolume: 0.0,
          wipVolume: 0.0
        },
        {
          label: 'Team_Mech_Audit',
          displayName: 'Team_Mech_Audit',
          targetVolume: 0.0,
          wipVolume: 0.0
        },
        {
          label: 'CRETURN_REPAIR_PREP',
          displayName: 'Repair Prep',
          targetVolume: 0.0,
          wipVolume: 0.0
        },
        {
          label: 'WAREHOUSE_DEALS_PS',
          displayName: 'WD Problem Solve',
          targetVolume: Math.round(197.0 * scaleFactor),
          wipVolume: 0.0
        },
        {
          label: 'CRETURN_PS_ITEM',
          displayName: 'C-Returns Prob Solve',
          targetVolume: Math.round(642.0 * scaleFactor),
          wipVolume: 0.0
        }
      ]
    };

    // LOCAL DEV: seed volume/WIP overrides for the Operator Override plan
    // (MOCK-PLAN-002) so the read-only Volume & Buffer tab on the published-plan
    // page shows populated override values. Match entries by label. The RCC
    // baseline plan keeps entries without override fields.
    if (this.isMockOverridePlan(params?.planId)) {
      const setOverrideByLabel = (
        list: any[],
        label: string,
        overrideVolume: number,
        overrideReason: string,
        wip?: { overrideWipVolume: number; wipOverrideReason: string }
      ) => {
        const entry = list.find((v) => v.label === label);
        if (entry) {
          entry.overrideVolume = overrideVolume;
          entry.overrideReason = overrideReason;
          entry.overrideComment = '';
          if (wip) {
            entry.overrideWipVolume = wip.overrideWipVolume;
            entry.wipOverrideReason = wip.wipOverrideReason;
            entry.wipOverrideComment = '';
          }
        }
      };
      setOverrideByLabel(
        dynamicVolumeData.primaryTargetAndWipVolume as any[],
        'CRETURN_PRIMARY_GRADING',
        Math.round(23756.0 * scaleFactor) + 1200,
        'Cube/Freight Mix projected',
        { overrideWipVolume: Math.round(22716.0 * scaleFactor) + 500, wipOverrideReason: 'Buffer for backlog' }
      );
      setOverrideByLabel(
        dynamicVolumeData.secondaryTargetAndWipVolume as any[],
        'CRETURN_REFURB',
        Math.round(885.0 * scaleFactor) + 300,
        'Special Event'
      );
    }
    
    console.log(`[Mock Data] Returning dynamic target and WIP volume data for warehouse: ${warehouseConfig.name} (scale factor: ${scaleFactor.toFixed(2)}, planId=${params?.planId}, override=${this.isMockOverridePlan(params?.planId)})`);
    return dynamicVolumeData;
  }

  /**
   * Get mock shift plan data with dynamic parameters
   * Supports both legacy string planId parameter and new MockRequestParams
   */
  static async getShiftPlan(paramsOrPlanId?: MockRequestParams | string): Promise<SPMSGetShiftPlanResponse> {
    await this.simulateDelay(350);
    
    // Handle backward compatibility - if string is passed, convert to params object
    let params: MockRequestParams | undefined;
    if (typeof paramsOrPlanId === 'string') {
      params = { planId: paramsOrPlanId };
    } else {
      params = paramsOrPlanId;
    }
    
    const warehouseConfig = this.getWarehouseConfig(params);
    const planId = params?.planId;
    
    if (planId) {
      const plan = mockShiftPlansList.find(p => p.planId === planId);
      if (plan) {
        const dynamicPlan = {
          ...plan,
          nodeId: warehouseConfig.nodeId,
          targetVolume: warehouseConfig.baseVolume,
          totalAssociates: warehouseConfig.associateCount,
          efficiency: warehouseConfig.efficiency,
          totalScheduledHours: warehouseConfig.associateCount * 8,
          dayShiftAssociates: Math.round(warehouseConfig.associateCount * 0.46),
          eveningShiftAssociates: Math.round(warehouseConfig.associateCount * 0.37),
          nightShiftAssociates: Math.round(warehouseConfig.associateCount * 0.17)
        };
        
        console.log(`[Mock Data] Returning dynamic shift plan for ${planId} at ${warehouseConfig.name}`);
        return dynamicPlan;
      }
    }
    
    const dynamicDefaultPlan = {
      ...mockShiftPlan,
      nodeId: warehouseConfig.nodeId,
      targetVolume: warehouseConfig.baseVolume,
      totalAssociates: warehouseConfig.associateCount,
      efficiency: warehouseConfig.efficiency,
      totalScheduledHours: warehouseConfig.associateCount * 8,
      dayShiftAssociates: Math.round(warehouseConfig.associateCount * 0.46),
      eveningShiftAssociates: Math.round(warehouseConfig.associateCount * 0.37),
      nightShiftAssociates: Math.round(warehouseConfig.associateCount * 0.17)
    };
    
    console.log(`[Mock Data] Returning dynamic default shift plan for ${warehouseConfig.name}`);
    return dynamicDefaultPlan;
  }

  /**
   * Get mock shift plans list
   */
  static async getAllShiftPlans(): Promise<SPMSGetShiftPlanResponse[]> {
    await this.simulateDelay(600);
    const { operatorPlanCount } = MOCK_SHIFT_PLANS_LIST;
    if (operatorPlanCount === null) {
      console.log('[Mock Data] Returning mock shift plans list');
      return [...mockShiftPlansList];
    }
    // Keep the RCC baseline plan(s) and only the first N operator (override) plans, using the same
    // baseline rule the page uses.
    const operatorPlans = getOverridePlans(mockShiftPlansList).slice(0, operatorPlanCount);
    const plans = mockShiftPlansList.filter((plan) => isBaselinePlan(plan) || operatorPlans.includes(plan));
    console.log(`[Mock Data] Returning mock shift plans list with ${operatorPlanCount} operator plan(s)`);
    return plans;
  }

  /**
   * Mock verify volumes response
   */
  static async verifyVolumes(): Promise<boolean> {
    await this.simulateDelay(800);
    console.log('[Mock Data] Returning mock verify volumes response');
    return true;
  }

  /**
   * Mock create shift plan response - simulates generating a new shift plan
   */
  static async createShiftPlan(params?: MockRequestParams): Promise<{ planId: string; status: string }> {
    await this.simulateDelay(1200); // Slightly longer delay to simulate processing
    
    const warehouseConfig = this.getWarehouseConfig(params);
    
    // Generate a new plan ID
    const timestamp = Date.now();
    const planId = `MOCK-PLAN-${timestamp}`;
    
    console.log(`[Mock Data] Creating new shift plan ${planId} for ${warehouseConfig.name}`);
    console.log(`[Mock Data] Plan parameters:`, {
      nodeId: params?.nodeId || warehouseConfig.nodeId,
      ofdDate: params?.ofdDate,
      planType: params?.planType,
      clockWindowName: params?.clockWindowName
    });
    
    return { 
      planId, 
      status: 'PENDING' // or PlanStatus.PENDING_USER_INPUT
    };
  }

  /**
   * Mock publish shift plan response
   */
  static async publishShiftPlan(planId: string, shiftType?: string): Promise<{ success: boolean; status: string; planId: string }> {
    await this.simulateDelay(1000);
    console.log(`[Mock Data] Publishing shift plan ${planId} with shiftType ${shiftType}`);
    return { success: true, status: "APPROVED", planId: planId || "mock-plan-id" };
  }

  /**
   * Mock GetPlanOverrides response — the persisted overrides a plan was generated
   * with. Only the checkout section is filled in: the plan summary page's other
   * read-only views fetch their own inputs through their page APIs.
   */
  static async getPlanOverrides(planId?: string): Promise<{
    planId: string;
    outputAdjustments: OutputAdjustmentEntry[];
  }> {
    await this.simulateDelay(500);
    return {
      planId: planId || 'mock-plan-id',
      outputAdjustments: [
        {
          functionId: 'func-001',
          functionName: 'Primary Grading',
          overridePercentage: 115,
          overrideReason: 'Volume forecast adjustment',
          disabled: false,
        },
        {
          functionId: 'func-002',
          functionName: 'Secondary Sort',
          overridePercentage: 90,
          overrideReason: 'Rate constraint',
          disabled: false,
        },
        {
          functionId: 'func-003',
          functionName: 'Trans Out Dock',
          overridePercentage: 100,
          overrideReason: 'Path not running this shift',
          disabled: true,
        },
      ],
    };
  }

  /**
   * Mock update shift plan response - simulates updating plan with input overrides
   */
  static async updateShiftPlan(planId: string, inputOverrides?: string): Promise<any> {
    await this.simulateDelay(800);
    
    console.log(`[Mock Data] Updating shift plan ${planId} with overrides:`, inputOverrides);
    
    // Return a response matching the SPMS format from the user feedback
    const currentTime = Date.now() / 1000; // Unix timestamp
    
    return {
      autoApproveOnCreation: null,
      clockWindowName: null,
      createdAt: currentTime - 1000, // Created 1 second ago
      dependentPlanId: null,
      errorCode: null,
      errorMessage: null,
      experimentMode: null,
      input: inputOverrides || null,
      isAutoPost: null,
      isAutoRun: null,
      laborTrackingStatus: null,
      lastUpdatedBy: "mock@amazon.com",
      nodeId: "TUS1", // Default node from user feedback
      nodeType: null,
      ofdDate: "2026-02-10", // Default date from user feedback
      onlyLaborOpportunities: null,
      output: null,
      planId: planId || "mock-plan-id",
      planType: "PRE_SHIFT",
      planVersion: "STATIC_V1",
      rejectReason: null,
      shiftPlanHorizonEndTime: null,
      shiftPlanHorizonStartTime: null,
      status: "RECEIVED_USER_INPUT", // Status from user feedback
      updatedAt: currentTime
    };
  }

  /**
   * Mock get shift plan output response - simulates polling behavior
   * Returns simple status for first 3-4 requests, detailed response on 5th retry
   */
  static async getShiftPlanOutput(params?: MockRequestParams): Promise<any> {
    await this.simulateDelay(60);
    
    const planId = params?.planId || "de7eb610-a735-44d0-ad69-4c0670c2dde7";
    
    // Increment request count for this planId
    if (!shiftPlanOutputRequestCounts[planId]) {
      shiftPlanOutputRequestCounts[planId] = 0;
    }
    shiftPlanOutputRequestCounts[planId]++;
    
    const requestCount = shiftPlanOutputRequestCounts[planId];

    console.log(`[Mock Data] getShiftPlanOutput request #${requestCount} for plan ${planId}`);

    // Pre-seeded plans are already in a final state, so skip the polling warmup
    // and return the detailed output immediately. Without this the first fetch on
    // page load lands on an empty shiftSummary and the table renders nothing until
    // enough refetches (e.g. changing the date) push the counter past the warmup.
    const isPreSeededPlan = mockShiftPlansList.some((plan) => plan.planId === planId);

    // Return simple status response for first 3-4 requests (generating plans only)
    if (!isPreSeededPlan && requestCount < 5) {
      return {
        planId: planId,
        planStatus: "RECEIVED_USER_INPUT",
        shiftSummary: [] // Add empty shiftSummary to prevent processShiftSummaryData errors
      };
    }
    
    // Return detailed response on 5th+ request
    const detailedResponse = {
      planId: planId,
      planStatus: "PENDING_APPROVAL",
      primaryGradingVolume: {
        recommended: { total: 19548.0, alps: 23755.0 },
        override: { total: 0.0, alps: 23755.0 }
      },
      // Two-fetch model: the baseline (RCC Proposed) output carries only the
      // recommended curve. The operator-override curve comes from the override
      // plan's OWN output (see MOCK_OPERATOR_OVERRIDE_PROJECTED below).
      projectedPrimaryGradingVolume: [
        { time: "07:00", recommended: 0.0 },
        { time: "08:00", recommended: 2331.0 },
        { time: "09:00", recommended: 4662.0 },
        { time: "10:00", recommended: 6993.0 },
        { time: "11:00", recommended: 8121.0 },
        { time: "12:00", recommended: 10446.0 },
        { time: "13:00", recommended: 11456.0 },
        { time: "14:00", recommended: 13934.0 },
        { time: "15:00", recommended: 15697.0 },
        { time: "16:00", recommended: 18455.0 },
        { time: "17:00", recommended: 20343.0 },
        { time: "17:30", recommended: 21766.0 }
      ],
      overrides: { total: 0.0, average: 0.0 },
      // Backend-computed override count; RCC baseline has none, set below for the
      // Operator Override plan.
      totalOverrides: 0,
      backlogProjections: {
        endOfShift: { total: 0.0, alps: 0.0 },
        endOfWeek: { total: 0.0, alps: 0.0 }
      },
      shiftTotals: {
        vetAcceptedHours: { intValue: 90.0 },
        vtoAcceptedHours: { intValue: 0.0 },
        amazonReadyAcceptedHours: { intValue: 100.0 },
        // showHoursNeeded = hours still NEEDED: positive = short-staffed (deficit),
        // matching the mock's 7.2% deficit. Negative would mean surplus.
        showHoursNeeded: { intValue: 238.0 },
        // V2 (UTR-REFLEX-1163): drives the Labor Hours Delta card pill. Percent is
        // always positive; the sign lives on showHoursNeeded above.
        laborHoursDeltaPercent: 7.2,
        // V2 Shift Totals (UTR-REFLEX-1163): regular scheduled + total available hours.
        regularScheduledHours: { intValue: 2586.0 },
        totalAvailableHours: { intValue: 2852.0 },
        vto: { actual: 0.0, recommended: 0.0 },
        vet: { actual: 29.0, recommended: 0.0 }
      },
      shiftNotes: [],
      shiftTimeline: [
        { label: "Start Shift", startTime: "09:00" },
        { label: "break 1", startTime: "9:15", endTime: "9:30" }
      ],
      shiftSummary: [
        {
          heading: "headcount",
          planOutput: [
            {
              startTime: "18:00",
              endTime: "19:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 131.0, hoursRequired: 131.98729549682542 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.98280956116159 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.0 }
              ]
            },
            {
              startTime: "19:00",
              endTime: "20:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 131.0, hoursRequired: 131.98729549682542 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.98280956116159 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.0 }
              ]
            },
            {
              startTime: "20:00",
              endTime: "21:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 131.0, hoursRequired: 131.98729549682542 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.98280956116159 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.0 }
              ]
            },
            {
              startTime: "21:00",
              endTime: "22:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 63.0, hoursRequired: 63.917424229895396 },
                { label: "WD Grading", intValue: 13.0, hoursRequired: 13.066952233116444 },
                { label: "Water Spider", intValue: 6.0, hoursRequired: 6.0 },
                { label: "C-Returns_Add-Ins", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Refurbish", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Sort", intValue: 4.0, hoursRequired: 4.0 },
                { label: "WD Water Spider", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Prob Solve", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Decant Non-TI", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.0 }
              ]
            },
            {
              startTime: "22:00",
              endTime: "23:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 131.0, hoursRequired: 131.6475737141638 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.913358572482476 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 0.0, hoursRequired: 0.3420075747483751 }
              ]
            },
            {
              startTime: "23:00",
              endTime: "00:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 129.0, hoursRequired: 129.90192717773783 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.55648749730679 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 2.0, hoursRequired: 2.413619414559132 }
              ]
            },
            {
              startTime: "00:00",
              endTime: "01:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 129.0, hoursRequired: 129.90192717773783 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.55648749730679 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 2.0, hoursRequired: 2.413619414559132 }
              ]
            },
            {
              startTime: "01:00",
              endTime: "02:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 62.0, hoursRequired: 62.813766309667294 },
                { label: "WD Grading", intValue: 12.0, hoursRequired: 12.841326036518614 },
                { label: "Water Spider", intValue: 6.0, hoursRequired: 6.0 },
                { label: "C-Returns_Add-Ins", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Refurbish", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Sort", intValue: 4.0, hoursRequired: 4.0 },
                { label: "WD Water Spider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Prob Solve", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Decant Non-TI", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 1.0, hoursRequired: 1.1670998972875548 }
              ]
            },
            {
              startTime: "02:00",
              endTime: "03:00",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 129.0, hoursRequired: 129.90192717773783 },
                { label: "WD Grading", intValue: 26.0, hoursRequired: 26.55648749730679 },
                { label: "Water Spider", intValue: 11.0, hoursRequired: 11.0 },
                { label: "C-Returns_Add-Ins", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Returns Refurbish", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Sort", intValue: 8.0, hoursRequired: 8.0 },
                { label: "WD Water Spider", intValue: 5.0, hoursRequired: 5.0 },
                { label: "C-Returns Prob Solve", intValue: 9.0, hoursRequired: 9.0 },
                { label: "Decant Non-TI", intValue: 8.0, hoursRequired: 8.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 8.0, hoursRequired: 8.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 2.0, hoursRequired: 2.413619414559132 }
              ]
            },
            {
              startTime: "03:00",
              endTime: "04:30",
              data: [
                { label: "C-Returns_Mezz", intValue: 3.0, hoursRequired: 3.0 },
                { label: "C-Returns Lead/PA", intValue: 6.0, hoursRequired: 6.0 },
                { label: "Team_Mech_Sort", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Wspider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Team_Mech_Unload", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Dock Scan", intValue: 3.0, hoursRequired: 3.0 },
                { label: "CR Refurb Spec 10", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Customer Returns 5S", intValue: 2.0, hoursRequired: 2.0 },
                { label: "Lead/PA - WD", intValue: 1.0, hoursRequired: 1.0 },
                { label: "Primary Grading", intValue: 62.0, hoursRequired: 62.813766309667294 },
                { label: "WD Grading", intValue: 12.0, hoursRequired: 12.841326036518614 },
                { label: "Water Spider", intValue: 6.0, hoursRequired: 6.0 },
                { label: "C-Returns_Add-Ins", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Refurbish", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Return Sort", intValue: 4.0, hoursRequired: 4.0 },
                { label: "WD Water Spider", intValue: 2.0, hoursRequired: 2.0 },
                { label: "C-Returns Prob Solve", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Decant Non-TI", intValue: 4.0, hoursRequired: 4.0 },
                { label: "Process Guide C-Ret", intValue: 2.0, hoursRequired: 2.3 },
                { label: "Unloader", intValue: 4.0, hoursRequired: 4.0 },
                { label: "C-Return Audit", intValue: 0.0, hoursRequired: 0.0 },
                { label: "Team_Mech_Audit", intValue: 1.0, hoursRequired: 1.1670998972875548 }
              ]
            }
          ]
        }
      ]
    };
    
    // Two-fetch model: an Operator Override plan is a separate plan in the list,
    // fetched by its own id. Its output carries the override plan's own curve and
    // metrics — the page overlays it on the RCC recommended line. Identified the
    // same way the app does: a plan NOT authored by ShiftPlanScheduler.
    const fetchedPlan = mockShiftPlansList.find((plan) => plan.planId === planId);
    const isOverridePlan =
      !!fetchedPlan &&
      String(fetchedPlan.lastUpdatedBy ?? '').toLowerCase().split('@')[0] !== 'shiftplanscheduler';
    if (isOverridePlan) {
      detailedResponse.projectedPrimaryGradingVolume = MOCK_OPERATOR_OVERRIDE_PROJECTED;
      // Override plan is a surplus case (distinct from the RCC baseline's deficit):
      // recommended volume sits ABOVE the ALPS target, so RCC Recommended Volume >
      // ALPS Planned Volume — keep these consistent (recommended.total > alps).
      detailedResponse.primaryGradingVolume = {
        recommended: { total: 24800.0, alps: 23755.0 },
        override: { total: 24800.0, alps: 23755.0 }
      };
      detailedResponse.shiftTotals = {
        ...detailedResponse.shiftTotals,
        // Negative showHoursNeeded => fewer hours needed than available => surplus
        // (over-staffed), exercising the surplus pill.
        showHoursNeeded: { intValue: -135.0 },
        laborHoursDeltaPercent: 4.1,
        vetAcceptedHours: { intValue: 60.0 },
        amazonReadyAcceptedHours: { intValue: 140.0 },
        vtoAcceptedHours: { intValue: 12.0 },
        // Override plan carries more available hours (the surplus story).
        regularScheduledHours: { intValue: 2586.0 },
        totalAvailableHours: { intValue: 2974.0 },
      };
      // Backend-computed override count for the Operator Override plan (6 here:
      // 2 target-primary + 1 target-secondary volume slots, 1 function report,
      // 1 roster row, + 1 total-attendance override).
      detailedResponse.totalOverrides = 6;
    }

    if (MOCK_MID_SHIFT_REPLAN.enabled) {
      console.log(`[Mock Data] Returning mid-shift re-plan output for plan ${planId} (override=${isOverridePlan})`);
      return buildMidShiftShiftPlanOutput(planId, isOverridePlan);
    }
    console.log(`[Mock Data] Returning detailed shift plan output for plan ${planId} (request #${requestCount}, override=${isOverridePlan})`);
    // The backend sends the same series on the new field and the deprecated one during the migration.
    return { ...detailedResponse, primaryGradingVolumeTrend: detailedResponse.projectedPrimaryGradingVolume };
  }

  /**
   * Get dark mode preference for local development
   * Reads from MOCK_DARK_MODE_ENABLED configuration toggle
   * Returns response in the same format as getDarkModeStatus() from bosonClient
   */
  static async getDarkModePreference(): Promise<{ args: { darkMode: string } }> {
    await this.simulateDelay(50);
    const darkMode = MOCK_DARK_MODE_ENABLED ? 'dark' : 'light';
    console.log(`[Mock Data] 🌙 Dark mode preference: ${darkMode} (MOCK_DARK_MODE_EN
      ABLED=${MOCK_DARK_MODE_ENABLED})`);
    return { args: { darkMode } };
  }

  /**
   * Mock trailer volume data for /listYardTrailers API
   * Returns data in ApiTrailerData format expected by the transformApiDataToTrailerData function
   */
  static async getInboundTrailerVolume(): Promise<any> {
    await this.simulateDelay(400);
    console.log('[Mock Data] Returning mock inbound trailer volume data');
    
    // Return data in ApiTrailerData format
    const mockTrailers = [
      {
        visitId: 'VISIT-001',
        trailerId: 'T-001',
        carrierId: 'FEDEX',
        location: 'BAY-A1',
        volume: {
          total: 2800,
          small: 1200,
          medium: 1000,
          large: 600,
          specialty: {
            tvs: 150,
            rugs: 75
          }
        },
        blocked: false,
        blockedReason: null,
        remainingItems: 420,
        tscore: 85.5,
        ymsNotes: 'Priority items - electronics'
      },
      {
        visitId: 'VISIT-002',
        trailerId: 'T-002',
        carrierId: 'UPS',
        location: 'BAY-B3',
        volume: {
          total: 3200,
          small: 1500,
          medium: 1200,
          large: 500,
          specialty: {
            tvs: 200,
            rugs: 100
          }
        },
        blocked: true,
        blockedReason: 'Safety inspection required',
        remainingItems: 800,
        tscore: 72.3,
        ymsNotes: 'Hold for inspection'
      },
      {
        visitId: 'VISIT-003',
        trailerId: 'T-003',
        carrierId: 'DHL',
        location: 'BAY-C2',
        volume: {
          total: 1850,
          small: 800,
          medium: 700,
          large: 350,
          specialty: {
            tvs: 80,
            rugs: 45
          }
        },
        blocked: false,
        blockedReason: null,
        remainingItems: 185,
        tscore: 91.2,
        ymsNotes: 'Express delivery items'
      },
      {
        visitId: 'VISIT-004',
        trailerId: 'T-004',
        carrierId: 'AMZL',
        location: 'BAY-D1',
        volume: {
          total: 4100,
          small: 2000,
          medium: 1500,
          large: 600,
          specialty: {
            tvs: 300,
            rugs: 150
          }
        },
        blocked: false,
        blockedReason: null,
        remainingItems: 1230,
        tscore: 88.7,
        ymsNotes: 'Amazon Logistics - standard processing'
      },
      {
        visitId: 'VISIT-005',
        trailerId: 'T-005',
        carrierId: 'XPO',
        location: 'BAY-E2',
        volume: {
          total: 2450,
          small: 1100,
          medium: 900,
          large: 450,
          specialty: {} // Empty specialty object - simulates production issue
        },
      blocked: false,
      blockedReason: null,
      remainingItems: 367,
      tscore: 79.3,
      ymsNotes: 'Standard freight - no specialty items'
    },
    {
      visitId: 'VISIT-006',
      trailerId: 'T-006',
      carrierId: 'USPS',
      location: 'BAY-F1',
      volume: {
        total: 0,
        small: 0,
        medium: 0,
        large: 0,
        specialty: { tvs: 0, rugs: 0 }
      },
      blocked: false,
      blockedReason: null,
      remainingItems: 0,
      tscore: 65.0,
      ymsNotes: 'Sealed trailer - volume unknown, needs manual entry'
    },
    {
      visitId: 'VISIT-007', trailerId: 'T-007', carrierId: 'FEDEX', location: 'BAY-G1',
      volume: { total: 1920, small: 850, medium: 720, large: 350, specialty: { tvs: 60, rugs: 30 } },
      blocked: false, blockedReason: null, remainingItems: 288, tscore: 82.1, ymsNotes: 'Standard mixed freight'
    },
    {
      visitId: 'VISIT-008', trailerId: 'T-008', carrierId: 'UPS', location: 'BAY-H2',
      volume: { total: 3650, small: 1700, medium: 1300, large: 650, specialty: { tvs: 180, rugs: 90 } },
      blocked: false, blockedReason: null, remainingItems: 912, tscore: 76.8, ymsNotes: 'Heavy electronics load'
    },
    {
      visitId: 'VISIT-009', trailerId: 'T-009', carrierId: 'DHL', location: 'BAY-A3',
      volume: { total: 2100, small: 950, medium: 800, large: 350, specialty: { tvs: 45, rugs: 20 } },
      blocked: false, blockedReason: null, remainingItems: 315, tscore: 89.4, ymsNotes: 'International returns'
    },
    {
      visitId: 'VISIT-010', trailerId: 'T-010', carrierId: 'AMZL', location: 'BAY-B1',
      volume: { total: 4500, small: 2200, medium: 1600, large: 700, specialty: { tvs: 250, rugs: 120 } },
      blocked: false, blockedReason: null, remainingItems: 1350, tscore: 93.2, ymsNotes: 'High priority - AMZL bulk'
    },
    {
      visitId: 'VISIT-011', trailerId: 'T-011', carrierId: 'XPO', location: 'BAY-C1',
      volume: { total: 1560, small: 700, medium: 580, large: 280, specialty: { tvs: 35, rugs: 15 } },
      blocked: true, blockedReason: 'RED TAGGED - safety concern', remainingItems: 234, tscore: 68.5, ymsNotes: 'RED TAGGED - unsafe dock condition'
    },
    {
      visitId: 'VISIT-012', trailerId: 'T-012', carrierId: 'FEDEX', location: 'BAY-D2',
      volume: { total: 2780, small: 1250, medium: 1050, large: 480, specialty: { tvs: 110, rugs: 55 } },
      blocked: false, blockedReason: null, remainingItems: 417, tscore: 84.7, ymsNotes: 'FedEx Ground - standard'
    },
    {
      visitId: 'VISIT-013', trailerId: 'T-013', carrierId: 'UPS', location: 'BAY-E1',
      volume: { total: 3100, small: 1400, medium: 1150, large: 550, specialty: { tvs: 170, rugs: 85 } },
      blocked: false, blockedReason: null, remainingItems: 775, tscore: 77.9, ymsNotes: 'UPS SurePost returns'
    },
    {
      visitId: 'VISIT-014', trailerId: 'T-014', carrierId: 'AMZL', location: 'BAY-F2',
      volume: { total: 0, small: 0, medium: 0, large: 0, specialty: { tvs: 0, rugs: 0 } },
      blocked: false, blockedReason: null, remainingItems: 0, tscore: 62.3, ymsNotes: 'Sealed - needs volume override'
    },
    {
      visitId: 'VISIT-015', trailerId: 'T-015', carrierId: 'DHL', location: 'BAY-G2',
      volume: { total: 1340, small: 600, medium: 500, large: 240, specialty: { tvs: 25, rugs: 10 } },
      blocked: false, blockedReason: null, remainingItems: 201, tscore: 86.1, ymsNotes: 'DHL Express returns'
    },
    {
      visitId: 'VISIT-016', trailerId: 'T-016', carrierId: 'FEDEX', location: 'BAY-H1',
      volume: { total: 2950, small: 1350, medium: 1100, large: 500, specialty: { tvs: 140, rugs: 70 } },
      blocked: false, blockedReason: null, remainingItems: 590, tscore: 81.3, ymsNotes: 'FedEx Express - priority items'
    },
    {
      visitId: 'VISIT-017', trailerId: 'T-017', carrierId: 'XPO', location: 'BAY-A2',
      volume: { total: 3800, small: 1800, medium: 1400, large: 600, specialty: { tvs: 220, rugs: 110 } },
      blocked: false, blockedReason: null, remainingItems: 950, tscore: 74.6, ymsNotes: 'YELLOW TAGGED - minor dock issue'
    },
    {
      visitId: 'VISIT-018', trailerId: 'T-018', carrierId: 'USPS', location: 'BAY-B2',
      volume: { total: 980, small: 450, medium: 360, large: 170, specialty: { tvs: 15, rugs: 8 } },
      blocked: false, blockedReason: null, remainingItems: 147, tscore: 90.5, ymsNotes: 'USPS Priority Mail returns'
    },
    {
      visitId: 'VISIT-019', trailerId: 'T-019', carrierId: 'AMZL', location: 'BAY-C3',
      volume: { total: 5200, small: 2500, medium: 1900, large: 800, specialty: { tvs: 350, rugs: 175 } },
      blocked: false, blockedReason: null, remainingItems: 1560, tscore: 95.0, ymsNotes: 'AMZL - highest priority bulk'
    },
    {
      visitId: 'VISIT-020', trailerId: 'T-020', carrierId: 'UPS', location: 'BAY-D3',
      volume: { total: 2200, small: 1000, medium: 830, large: 370, specialty: { tvs: 95, rugs: 48 } },
      blocked: false, blockedReason: null, remainingItems: 550, tscore: 78.2, ymsNotes: 'UPS Ground - mixed returns'
    },
    {
      visitId: 'VISIT-021', trailerId: 'T-021', carrierId: 'FEDEX', location: 'BAY-E3',
      volume: { total: 1680, small: 750, medium: 630, large: 300, specialty: { tvs: 50, rugs: 25 } },
      blocked: false, blockedReason: null, remainingItems: 252, tscore: 83.9, ymsNotes: 'FedEx SmartPost returns'
    }
  ];

    return {
      trailers: mockTrailers
    };
  }

  /**
   * Returns sample indirect staffing configuration data.
   */
  static async getIndirectStaffingConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    return {
      warehouseId: params?.warehouseId || 'LAS2',
      indirectStaffingRules: [
        {
          department: 'Reverse Logistics',
          processPath: 'C-Returns Support',
          function: 'C-Returns Lead/PA',
          directRoleMapping: 'Customer Returns',
          description: 'Lead/PA support for C-Returns',
          ruleList: [
            {
              priority: 1,
              ruleType: 'FOR_EVERY' as const,
              ruleAppliesOn: 'Shift',
              directBaseRatio: 15,
              indirectRequirementRatio: 1,
              lever: null,
            },
          ],
        },
        {
          department: 'Inbound',
          processPath: 'Transfer In and Stow',
          function: 'Water Spider',
          directRoleMapping: 'Stow Associates',
          description: 'Water spider support for stow',
          ruleList: [
            {
              priority: 1,
              ruleType: 'ALWAYS_HAVE' as const,
              ruleAppliesOn: 'N/A',
              directBaseRatio: null,
              indirectRequirementRatio: 2,
              lever: null,
            },
          ],
        },
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Support',
          function: 'Team_Mech_P-Solver',
          directRoleMapping: 'Primary Grading',
          description: 'Problem solve on the east side of the building. They are to process work that gets escalated to them.',
          ruleList: [
            {
              priority: 1,
              ruleType: 'FOR_EVERY' as const,
              ruleAppliesOn: 'TEAM_MECH_AA',
              directBaseRatio: 20,
              indirectRequirementRatio: 2,
              lever: null,
            },
          ],
        },
      ],
    };
  }

  /**
   * Returns a success response for updating indirect staffing configuration.
   */
  static async updateIndirectStaffingConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);
    return {
      status: 'PROMOTED',
      stageVersionName: (params?.warehouseId || 'LAS2') + '-mockuser-20260413143526',
      message: 'Changes promoted to live config',
      processedCount: params?.changes?.length || 1,
      failedCount: 0
    };
  }

  /**
   * Returns mock drafts list for a warehouse.
   */
  static async listConfigDrafts(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    const wh = params?.warehouseId || 'LAS2';
    return {
      warehouseId: wh,
      configType: params?.configType || 'INDIRECT_STAFFING',
      drafts: [
        {
          stageVersionName: wh + '-dhaanjay-20260413143526',
          createdBy: 'dhaanjay@amazon.com',
          createdAt: '2026-04-13T14:35:26Z',
          changeCount: 2,
        },
        {
          stageVersionName: wh + '-johndoe-20260412091500',
          createdBy: 'johndoe@amazon.com',
          createdAt: '2026-04-12T09:15:00Z',
          changeCount: 1,
        },
      ],
    };
  }

  /**
   * Returns mock promote response.
   */
  static async promoteConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);
    return {
      status: 'SUCCESS',
      message: 'Successfully promoted ' + (params?.stageVersionName || 'draft') + ' to live config',
    };
  }

  /**
   * Returns mock flow path percentage configuration for a warehouse.
   */
  static async getFlowPathPercentageConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    const warehouseId = params?.warehouseId || 'LAS2';
    return {
      flowPathEntries: [
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Processed',
          function: 'Advanced Grading Lite 1',
          fclmCode: 4300016841,
          flowPathPercentage: 15,
        },
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Processed',
          function: 'Advanced Grading Lite 2',
          fclmCode: 4300016839,
          flowPathPercentage: 10,
        },
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Processed',
          function: 'MLG Fast Processing',
          fclmCode: 1766442445256,
          flowPathPercentage: 25,
        },
        {
          department: 'Customer Returns',
          processPath: 'C-Returns Processed',
          function: 'Advanced Grading Plus',
          fclmCode: 1617058767035,
          flowPathPercentage: 20,
        },
        {
          department: 'Warehouse Deals',
          processPath: 'WHD Grading',
          function: 'WD Grading - AR',
          fclmCode: 4300006832,
          flowPathPercentage: 30,
        },
      ],
    };
  }

  /**
   * Mock response for GetWorkItemCounts.
   *
   * `pendingTasksCount` and `highPriorityCount` are derived as the sum of
   * their per-process-path counterparts so the dashboard totals reconcile
   * with the per-path cards.
   */
  static async getWorkItemCounts(
    _params?: MockRequestParams
  ): Promise<GetWorkItemCountsResponse> {
    await this.simulateDelay(DEFAULT_DELAY_MS);
    console.log('[Mock Data] Returning mock work item counts');

    const processPathCounts = [
      { processPath: 'Yard', totalCount: 12, highPriorityCount: 3 },
      { processPath: 'Problem Solve', totalCount: 20, highPriorityCount: 4 },
      { processPath: 'Clean Decant', totalCount: 15, highPriorityCount: 2 },
      { processPath: 'Warehouse Deals', totalCount: 7, highPriorityCount: 1 },
    ];

    const pendingTasksCount = processPathCounts.reduce(
      (acc, entry) => acc + entry.totalCount,
      0,
    );
    const highPriorityCount = processPathCounts.reduce(
      (acc, entry) => acc + entry.highPriorityCount,
      0,
    );

    return {
      pendingTasksCount,
      highPriorityCount,
      processPathCounts,
      completedTodayCount: 132,
      expiredTodayCount: 6,
    };
  }

  /**
   * Returns a success response for updating warehouse operational configuration.
   */
  static async updateWarehouseOperationalConfig(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    const changeCount = params?.changes?.length || 1;
    return {
      status: 'PROMOTED',
      stageVersionName: (params?.warehouseId || 'LAS2') + '-mockuser-' + Date.now(),
      message: changeCount + ' changes promoted to live config',
      processedCount: changeCount,
      failedCount: 0,
    };
  }

  /**
   * Mock response for GetWorkItems.
   *
   * Cycles deterministically through three branches so local dev exercises
   * every path in the frontend's buffer logic:
   *   - overflow   (every 3rd call)  → pageSize + 3 items with a nextToken
   *   - end-of-list(every 5th call)  → fewer than pageSize items, no nextToken
   *   - happy path (all others)      → exactly pageSize items with a nextToken
   *
   * Passing `nextToken === "final"` forces the end-of-list branch on demand.
   */
  static async getWorkItems(params: {
    processPaths?: string[];
    pageSize?: number;
    nextToken?: string;
  }): Promise<GetWorkItemsResponse> {
    await this.simulateDelay(DEFAULT_DELAY_MS);

    const pageSize = params?.pageSize ?? WORK_ITEMS_PAGE_SIZE;
    const processPaths = params?.processPaths ?? [];
    const callNumber = ++workItemsCallCounter;

    // Branch selection:
    //   - forced end-of-list when caller passes nextToken === "final"
    //   - end-of-list on every 5th call
    //   - overflow on every 3rd call (but not when it overlaps with 5th)
    //   - happy path otherwise
    const defaultProcessPathRotation = ['Yard', 'Problem Solve', 'Clean Decant', 'Warehouse Deals'];
    const severityRotation = ['HIGH', 'MEDIUM', 'LOW'];

    const isForcedEnd = params?.nextToken === 'final';
    const isEndOfList = isForcedEnd || callNumber % 5 === 0;
    const isOverflow = !isEndOfList && callNumber % 3 === 0;

    let itemCount: number;
    if (isEndOfList) {
      // Fewer than pageSize items. Use max(1, pageSize - 3) so we still have
      // some rows to render; drop to 0 when the caller specifies pageSize <= 1.
      itemCount = Math.max(0, Math.min(pageSize - 3, pageSize - 1));
    } else if (isOverflow) {
      itemCount = pageSize + 3;
    } else {
      itemCount = pageSize;
    }

    const workItems: WorkItemSummary[] = [];
    for (let i = 0; i < itemCount; i++) {
      const processPath = processPaths.length > 0
        ? processPaths[0]
        : defaultProcessPathRotation[i % defaultProcessPathRotation.length];

      const severity = severityRotation[(callNumber + i) % severityRotation.length];

      // lastWorkItemStatusUpdatedTime spans the last 0–20 hours so the AGE
      // column renders a wide distribution of values in dev. Random is
      // acceptable for a dev-only mock.
      const hoursAgo = Math.floor(Math.random() * 20);
      const minutesAgo = Math.floor(Math.random() * 60);
      const lastWorkItemStatusUpdatedTime =
        Date.now() - (hoursAgo * 60 * 60 * 1000 + minutesAgo * 60 * 1000);

      // Operator-mix invariant for the Work Item Detail Page flow:
      // every non-empty batch emits at least one row with an
      // empty operator (exercises the "Start Task" path) and at least one
      // row with a populated operator (exercises the "Continue Task"
      // path). Indices 0 and 1 are dedicated to those two cases; the
      // remaining rows rotate through `mock.operator1..5` to keep
      // operator variety visible in dev.
      let operator: string;
      if (i === 0) {
        operator = '';
      } else if (i === 1) {
        operator = 'mock.operator.alice';
      } else {
        operator = `mock.operator${(i % 5) + 1}`;
      }

      workItems.push({
        workItemId: `mock-item-${callNumber}-${i}`,
        severity,
        exceptionName: `Mock exception ${callNumber}-${i}`,
        processPath,
        operator,
        lastWorkItemStatusUpdatedTime,
      });
    }

    const response: GetWorkItemsResponse = {
      workItems,
      ...(isEndOfList ? {} : { nextToken: `mock-next-token-${callNumber}` }),
    };

    console.log(
      `[Mock Data] getWorkItems call #${callNumber}: branch=${
        isEndOfList ? 'end-of-list' : isOverflow ? 'overflow' : 'happy'
      }, items=${workItems.length}, nextToken=${response.nextToken ?? 'null'}`
    );

    return response;
  }

  /**
   * Test-only helper: resets the deterministic rotation counter used by
   * `getWorkItems` so test cases can start from a known state.
   */
  static __resetWorkItemsCallCounter(): void {
    workItemsCallCounter = 0;
  }

  /**
   * Mock response for `LoadWorkItem`.
   *
   * Resolves any `workItemId` to a fully populated `WorkItem` shape so the
   * Start → detail-page flow is exercisable end-to-end against mocks.
   * Finite epoch-ms timestamps keep the
   * `Timestamp_Formatter` from rendering "—" placeholders in dev.
   *
   * The resolver is deterministic: for a given `workItemId`, the same
   * payload is returned every call. Unknown ids (outside the
   * `mock-item-*` family emitted by `getWorkItems`) fall through to a
   * generic shape-valid stub with the same invariants.
   */
  static async loadWorkItem(workItemId: string): Promise<LoadWorkItemResponse> {
    await this.simulateDelay(DEFAULT_DELAY_MS);

    // Hash-ish derivation so the stub varies per id but stays stable for
    // repeat calls with the same id.
    let hash = 0;
    for (let i = 0; i < workItemId.length; i++) {
      hash = (hash * 31 + workItemId.charCodeAt(i)) | 0;
    }
    const positive = Math.abs(hash);

    const severityRotation = ['HIGH', 'MEDIUM', 'LOW'];
    const processRotation = ['Yard', 'Problem Solve', 'Clean Decant', 'Warehouse Deals'];
    const grainRotation = ['Order', 'Shipment', 'Unit', 'Container'];

    // Fixed epoch-ms anchor + per-id offsets so the payload is
    // deterministic and the three timestamps form a chronological
    // sequence (created < updated < lastStatusUpdated).
    const anchor = Date.UTC(2024, 0, 15, 9, 30, 45); // 2024-01-15 09:30:45 UTC
    const createdTime = anchor + (positive % 100) * 60 * 1000;
    const updatedTime = createdTime + 15 * 60 * 1000;
    const lastWorkItemStatusUpdatedTime = updatedTime + 15 * 60 * 1000;

    const workItem: WorkItem = {
      workItemId,
      exceptionName: `Mock exception for ${workItemId}`,
      severity: severityRotation[positive % severityRotation.length],
      status: 'ACTIVE',
      processPath: processRotation[positive % processRotation.length],
      process: processRotation[(positive + 1) % processRotation.length],
      operator: positive % 2 === 0 ? '' : 'mock.operator.alice',
      grainType: grainRotation[positive % grainRotation.length],
      grainId: `GRAIN-${positive % 10_000}`,
      actionStatus: 'OPEN',
      description: `Investigate mock exception ${workItemId}.`,
      createdTime,
      updatedTime,
      lastWorkItemStatusUpdatedTime,
      additionalAttributes: {
        lane: `LANE-${(positive % 12) + 1}`,
        batch: `BATCH-${(positive % 99) + 1}`,
        sourceSystem: 'MockDataService',
      },
    };

    console.log(
      `[Mock Data] loadWorkItem resolved workItemId=${workItemId}`,
    );

    return { workItem };
  }

  /**
   * Mock response for `UpdateWorkItemState`.
   *
   * Returns a representative success payload for both the "Start/Continue"
   * (ACTIVE) and "Resolve" (RESOLVED) call shapes. The method
   * intentionally never fails in mock mode — error-path coverage is
   * exercised by the frontend tests that mock `fetchUpdateWorkItemState`
   * directly.
   */
  static async updateWorkItemState(
    body: UpdateWorkItemStateRequest,
  ): Promise<UpdateWorkItemStateResponse> {
    await this.simulateDelay(DEFAULT_DELAY_MS);

    console.log(
      `[Mock Data] updateWorkItemState workItemId=${body.workItemId}, status=${body.status}, operator=${body.operator}`,
    );

    return {
      message: `Mock updateWorkItemState succeeded for ${body.workItemId}`,
      responseStatus: 'SUCCESS',
       };
      }
  /**
   * Returns mock compare flow monitoring data for local testing.
   */
  static async compareFlowMonitoringData(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);
    // Sample of a real compareFlowMonitoringData response so local dev exercises
    // the dashboard against realistic, varied data — 7 process paths (mix of
    // AHEAD / ON_TRACK / AT_RISK / CRITICAL, zero-volume support paths, and
    // expected=0 edge cases) across 5 hourly windows.
    const shiftTotalsComparison = [
      {
        monitoringEntityIdentity: { processPath: { processId: "1003026", functionId: "1667843456854", processName: "C-Returns Processed", displayName: "Primary Grading" } },
        monitoringEntityType: "PROCESS_PATH",
        metricsComparisonList: [
          { metricName: "UPH", actualValue: 14.95, expectedValue: 16.33, delta: -1.39, variancePercentage: -8.49, comparisonStatus: "AT_RISK" },
          { metricName: "VOLUME_PROCESSED", actualValue: 23699, expectedValue: 32046.17, delta: -8347.17, variancePercentage: -26.05, comparisonStatus: "CRITICAL" },
          { metricName: "HEADCOUNT", actualValue: 151, expectedValue: 214, delta: -63, variancePercentage: -29.44, comparisonStatus: "CRITICAL" },
          { metricName: "HOURS", actualValue: 1638.06, expectedValue: 1961.87, delta: -323.81, variancePercentage: -16.51, comparisonStatus: "CRITICAL" },
        ],
      },
      {
        monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006653", processName: "C-Returns Support", displayName: "C-Returns Lead/PA" } },
        monitoringEntityType: "PROCESS_PATH",
        metricsComparisonList: [
          { metricName: "UPH", actualValue: 1.86, expectedValue: 0.46, delta: 1.4, variancePercentage: 305.68, comparisonStatus: "AHEAD" },
          { metricName: "VOLUME_PROCESSED", actualValue: 254, expectedValue: 26.15, delta: 227.85, variancePercentage: 871.49, comparisonStatus: "AHEAD" },
          { metricName: "HEADCOUNT", actualValue: 13, expectedValue: 6, delta: 7, variancePercentage: 116.67, comparisonStatus: "AHEAD" },
          { metricName: "HOURS", actualValue: 134.98, expectedValue: 57, delta: 77.98, variancePercentage: 136.81, comparisonStatus: "AHEAD" },
        ],
      },
      {
        monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006654", processName: "C-Returns Support", displayName: "C-Returns Prob Solve" } },
        monitoringEntityType: "PROCESS_PATH",
        metricsComparisonList: [
          { metricName: "UPH", actualValue: 7.1, expectedValue: 7.25, delta: -0.15, variancePercentage: -2.01, comparisonStatus: "ON_TRACK" },
          { metricName: "VOLUME_PROCESSED", actualValue: 895, expectedValue: 888.79, delta: 6.21, variancePercentage: 0.7, comparisonStatus: "ON_TRACK" },
          { metricName: "HEADCOUNT", actualValue: 12, expectedValue: 14, delta: -2, variancePercentage: -14.29, comparisonStatus: "AT_RISK" },
          { metricName: "HOURS", actualValue: 123, expectedValue: 122.62, delta: 0.38, variancePercentage: 0.31, comparisonStatus: "ON_TRACK" },
        ],
      },
      {
        monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300016758", processName: "C-Returns Support", displayName: "C-Returns Refurbish" } },
        monitoringEntityType: "PROCESS_PATH",
        metricsComparisonList: [
          { metricName: "UPH", actualValue: 11.84, expectedValue: 10.85, delta: 0.99, variancePercentage: 9.13, comparisonStatus: "AHEAD" },
          { metricName: "VOLUME_PROCESSED", actualValue: 373, expectedValue: 612.55, delta: -239.55, variancePercentage: -39.11, comparisonStatus: "CRITICAL" },
          { metricName: "HEADCOUNT", actualValue: 3, expectedValue: 6, delta: -3, variancePercentage: -50, comparisonStatus: "CRITICAL" },
          { metricName: "HOURS", actualValue: 30.68, expectedValue: 56.45, delta: -25.77, variancePercentage: -45.65, comparisonStatus: "CRITICAL" },
        ],
      },
      {
        monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234561862", processName: "C-Returns Support", displayName: "Team_Mech_Sort" } },
        monitoringEntityType: "PROCESS_PATH",
        metricsComparisonList: [
          { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
          { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
          { metricName: "HEADCOUNT", actualValue: 6, expectedValue: 2, delta: 4, variancePercentage: 200, comparisonStatus: "AHEAD" },
          { metricName: "HOURS", actualValue: 60.84, expectedValue: 19, delta: 41.84, variancePercentage: 220.21, comparisonStatus: "AHEAD" },
        ],
      },
      {
        monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599235212848", processName: "C-Returns Support", displayName: "C-Returns_Add-Ins" } },
        monitoringEntityType: "PROCESS_PATH",
        metricsComparisonList: [
          { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
          { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
          { metricName: "HEADCOUNT", actualValue: 0, expectedValue: 6, delta: -6, variancePercentage: -100, comparisonStatus: "CRITICAL" },
          { metricName: "HOURS", actualValue: 0, expectedValue: 54.5, delta: -54.5, variancePercentage: -100, comparisonStatus: "CRITICAL" },
        ],
      },
      {
        monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234929207", processName: "C-Returns Support", displayName: "Team_Mech_Audit" } },
        monitoringEntityType: "PROCESS_PATH",
        metricsComparisonList: [
          { metricName: "UPH", actualValue: 10.28, expectedValue: 11.14, delta: -0.87, variancePercentage: -7.77, comparisonStatus: "AT_RISK" },
          { metricName: "VOLUME_PROCESSED", actualValue: 1079, expectedValue: 0, delta: 1079, variancePercentage: 0, comparisonStatus: "AHEAD" },
          { metricName: "HEADCOUNT", actualValue: 10, expectedValue: 0, delta: 10, variancePercentage: 0, comparisonStatus: "AHEAD" },
          { metricName: "HOURS", actualValue: 101.33, expectedValue: 0, delta: 101.33, variancePercentage: 0, comparisonStatus: "AHEAD" },
        ],
      },
    ];

    const hourlyShiftComparisonDataList = [
      {
        startTime: "18:00", endTime: "19:00",
        monitoringEntityIdentitiesComparisonDataList: [
          {
            monitoringEntityIdentity: { processPath: { processId: "1003026", functionId: "1667843456854", processName: "C-Returns Processed", displayName: "Primary Grading" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 12.49, expectedValue: 16.33, delta: -3.85, variancePercentage: -23.55, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 2123, expectedValue: 3495.58, delta: -1372.58, variancePercentage: -39.27, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 170, expectedValue: 214, delta: -44, variancePercentage: -20.56, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 167.87, expectedValue: 214, delta: -46.13, variancePercentage: -21.56, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006653", processName: "C-Returns Support", displayName: "C-Returns Lead/PA" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0.46, delta: -0.46, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 2.75, delta: -2.75, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 13, expectedValue: 6, delta: 7, variancePercentage: 116.67, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 12.58, expectedValue: 6, delta: 6.58, variancePercentage: 109.72, comparisonStatus: "AHEAD" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006654", processName: "C-Returns Support", displayName: "C-Returns Prob Solve" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 6.92, expectedValue: 7.25, delta: -0.33, variancePercentage: -4.58, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 83, expectedValue: 96.95, delta: -13.95, variancePercentage: -14.39, comparisonStatus: "AT_RISK" },
              { metricName: "HEADCOUNT", actualValue: 12, expectedValue: 14, delta: -2, variancePercentage: -14.29, comparisonStatus: "AT_RISK" },
              { metricName: "HOURS", actualValue: 11.85, expectedValue: 13.38, delta: -1.53, variancePercentage: -11.4, comparisonStatus: "AT_RISK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300016758", processName: "C-Returns Support", displayName: "C-Returns Refurbish" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 10.85, delta: -10.85, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 64.5, delta: -64.5, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 3, expectedValue: 6, delta: -3, variancePercentage: -50, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 2.87, expectedValue: 5.94, delta: -3.08, variancePercentage: -51.78, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234561862", processName: "C-Returns Support", displayName: "Team_Mech_Sort" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 2, expectedValue: 2, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HOURS", actualValue: 1.89, expectedValue: 2, delta: -0.11, variancePercentage: -5.57, comparisonStatus: "AT_RISK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599235212848", processName: "C-Returns Support", displayName: "C-Returns_Add-Ins" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 0, expectedValue: 6, delta: -6, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 0, expectedValue: 5.94, delta: -5.94, variancePercentage: -100, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234929207", processName: "C-Returns Support", displayName: "Team_Mech_Audit" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 10.22, expectedValue: 11.14, delta: -0.92, variancePercentage: -8.26, comparisonStatus: "AT_RISK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 92, expectedValue: 0, delta: 92, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 9, expectedValue: 0, delta: 9, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 8.02, expectedValue: 0, delta: 8.02, variancePercentage: 0, comparisonStatus: "AHEAD" },
            ],
          }
        ],
      },
      {
        startTime: "19:00", endTime: "20:00",
        monitoringEntityIdentitiesComparisonDataList: [
          {
            monitoringEntityIdentity: { processPath: { processId: "1003026", functionId: "1667843456854", processName: "C-Returns Processed", displayName: "Primary Grading" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 17.96, expectedValue: 16.33, delta: 1.63, variancePercentage: 9.96, comparisonStatus: "AHEAD" },
              { metricName: "VOLUME_PROCESSED", actualValue: 3323, expectedValue: 3495.58, delta: -172.58, variancePercentage: -4.94, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 185, expectedValue: 214, delta: -29, variancePercentage: -13.55, comparisonStatus: "AT_RISK" },
              { metricName: "HOURS", actualValue: 184.49, expectedValue: 214, delta: -29.51, variancePercentage: -13.79, comparisonStatus: "AT_RISK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006653", processName: "C-Returns Support", displayName: "C-Returns Lead/PA" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0.46, delta: -0.46, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 2.75, delta: -2.75, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 13, expectedValue: 6, delta: 7, variancePercentage: 116.67, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 13, expectedValue: 6, delta: 7, variancePercentage: 116.67, comparisonStatus: "AHEAD" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006654", processName: "C-Returns Support", displayName: "C-Returns Prob Solve" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 9.92, expectedValue: 7.25, delta: 2.67, variancePercentage: 36.9, comparisonStatus: "AHEAD" },
              { metricName: "VOLUME_PROCESSED", actualValue: 129, expectedValue: 96.95, delta: 32.05, variancePercentage: 33.06, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 13, expectedValue: 14, delta: -1, variancePercentage: -7.14, comparisonStatus: "AT_RISK" },
              { metricName: "HOURS", actualValue: 12.67, expectedValue: 13.38, delta: -0.7, variancePercentage: -5.24, comparisonStatus: "AT_RISK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300016758", processName: "C-Returns Support", displayName: "C-Returns Refurbish" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 8.67, expectedValue: 10.85, delta: -2.18, variancePercentage: -20.13, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 26, expectedValue: 64.5, delta: -38.5, variancePercentage: -59.69, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 3, expectedValue: 6, delta: -3, variancePercentage: -50, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 3, expectedValue: 5.94, delta: -2.94, variancePercentage: -49.53, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234561862", processName: "C-Returns Support", displayName: "Team_Mech_Sort" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 2, expectedValue: 2, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HOURS", actualValue: 1.76, expectedValue: 2, delta: -0.24, variancePercentage: -12.22, comparisonStatus: "AT_RISK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599235212848", processName: "C-Returns Support", displayName: "C-Returns_Add-Ins" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 0, expectedValue: 6, delta: -6, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 0, expectedValue: 5.94, delta: -5.94, variancePercentage: -100, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234929207", processName: "C-Returns Support", displayName: "Team_Mech_Audit" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 11.36, expectedValue: 11.14, delta: 0.22, variancePercentage: 1.98, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 125, expectedValue: 0, delta: 125, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 11, expectedValue: 0, delta: 11, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 10.23, expectedValue: 0, delta: 10.23, variancePercentage: 0, comparisonStatus: "AHEAD" },
            ],
          }
        ],
      },
      {
        startTime: "20:00", endTime: "21:00",
        monitoringEntityIdentitiesComparisonDataList: [
          {
            monitoringEntityIdentity: { processPath: { processId: "1003026", functionId: "1667843456854", processName: "C-Returns Processed", displayName: "Primary Grading" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 15.73, expectedValue: 16.33, delta: -0.61, variancePercentage: -3.71, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 3020, expectedValue: 3495.58, delta: -475.58, variancePercentage: -13.61, comparisonStatus: "AT_RISK" },
              { metricName: "HEADCOUNT", actualValue: 192, expectedValue: 214, delta: -22, variancePercentage: -10.28, comparisonStatus: "AT_RISK" },
              { metricName: "HOURS", actualValue: 189, expectedValue: 214, delta: -25, variancePercentage: -11.68, comparisonStatus: "AT_RISK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006653", processName: "C-Returns Support", displayName: "C-Returns Lead/PA" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0.85, expectedValue: 0.46, delta: 0.39, variancePercentage: 84.47, comparisonStatus: "AHEAD" },
              { metricName: "VOLUME_PROCESSED", actualValue: 11, expectedValue: 2.75, delta: 8.25, variancePercentage: 299.69, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 13, expectedValue: 6, delta: 7, variancePercentage: 116.67, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 13, expectedValue: 6, delta: 7, variancePercentage: 116.67, comparisonStatus: "AHEAD" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006654", processName: "C-Returns Support", displayName: "C-Returns Prob Solve" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 9, expectedValue: 7.25, delta: 1.75, variancePercentage: 24.16, comparisonStatus: "AHEAD" },
              { metricName: "VOLUME_PROCESSED", actualValue: 117, expectedValue: 96.95, delta: 20.05, variancePercentage: 20.68, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 13, expectedValue: 14, delta: -1, variancePercentage: -7.14, comparisonStatus: "AT_RISK" },
              { metricName: "HOURS", actualValue: 12.46, expectedValue: 13.38, delta: -0.92, variancePercentage: -6.85, comparisonStatus: "AT_RISK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300016758", processName: "C-Returns Support", displayName: "C-Returns Refurbish" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 16.33, expectedValue: 10.85, delta: 5.48, variancePercentage: 50.53, comparisonStatus: "AHEAD" },
              { metricName: "VOLUME_PROCESSED", actualValue: 49, expectedValue: 64.5, delta: -15.5, variancePercentage: -24.03, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 3, expectedValue: 6, delta: -3, variancePercentage: -50, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 3, expectedValue: 5.94, delta: -2.94, variancePercentage: -49.53, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234561862", processName: "C-Returns Support", displayName: "Team_Mech_Sort" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 1, expectedValue: 2, delta: -1, variancePercentage: -50, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 0.36, expectedValue: 2, delta: -1.64, variancePercentage: -81.81, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599235212848", processName: "C-Returns Support", displayName: "C-Returns_Add-Ins" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 0, expectedValue: 6, delta: -6, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 0, expectedValue: 5.94, delta: -5.94, variancePercentage: -100, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234929207", processName: "C-Returns Support", displayName: "Team_Mech_Audit" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 14.17, expectedValue: 11.14, delta: 3.02, variancePercentage: 27.14, comparisonStatus: "AHEAD" },
              { metricName: "VOLUME_PROCESSED", actualValue: 170, expectedValue: 0, delta: 170, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 12, expectedValue: 0, delta: 12, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 11.68, expectedValue: 0, delta: 11.68, variancePercentage: 0, comparisonStatus: "AHEAD" },
            ],
          }
        ],
      },
      {
        startTime: "21:00", endTime: "22:00",
        monitoringEntityIdentitiesComparisonDataList: [
          {
            monitoringEntityIdentity: { processPath: { processId: "1003026", functionId: "1667843456854", processName: "C-Returns Processed", displayName: "Primary Grading" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 14.52, expectedValue: 16.33, delta: -1.82, variancePercentage: -11.13, comparisonStatus: "AT_RISK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 1829, expectedValue: 1736.65, delta: 92.35, variancePercentage: 5.32, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 126, expectedValue: 213, delta: -87, variancePercentage: -40.85, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 122.64, expectedValue: 106.32, delta: 16.32, variancePercentage: 15.35, comparisonStatus: "AHEAD" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006653", processName: "C-Returns Support", displayName: "C-Returns Lead/PA" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0.46, delta: -0.46, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 1.38, delta: -1.38, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 10, expectedValue: 6, delta: 4, variancePercentage: 66.67, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 9.2, expectedValue: 3, delta: 6.2, variancePercentage: 206.67, comparisonStatus: "AHEAD" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006654", processName: "C-Returns Support", displayName: "C-Returns Prob Solve" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 4.42, expectedValue: 7.25, delta: -2.83, variancePercentage: -39.07, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 53, expectedValue: 48.17, delta: 4.83, variancePercentage: 10.04, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 12, expectedValue: 14, delta: -2, variancePercentage: -14.29, comparisonStatus: "AT_RISK" },
              { metricName: "HOURS", actualValue: 11.17, expectedValue: 6.64, delta: 4.52, variancePercentage: 68.05, comparisonStatus: "AHEAD" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300016758", processName: "C-Returns Support", displayName: "C-Returns Refurbish" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 10.67, expectedValue: 10.85, delta: -0.18, variancePercentage: -1.69, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 32, expectedValue: 32.04, delta: -0.04, variancePercentage: -0.14, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 3, expectedValue: 6, delta: -3, variancePercentage: -50, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 3, expectedValue: 2.95, delta: 0.05, variancePercentage: 1.58, comparisonStatus: "ON_TRACK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234561862", processName: "C-Returns Support", displayName: "Team_Mech_Sort" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 0, expectedValue: 2, delta: -2, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 0, expectedValue: 1, delta: -1, variancePercentage: -100, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599235212848", processName: "C-Returns Support", displayName: "C-Returns_Add-Ins" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 0, expectedValue: 6, delta: -6, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 0, expectedValue: 2.95, delta: -2.95, variancePercentage: -100, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234929207", processName: "C-Returns Support", displayName: "Team_Mech_Audit" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 9.44, expectedValue: 11.14, delta: -1.7, variancePercentage: -15.24, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 85, expectedValue: 0, delta: 85, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 9, expectedValue: 0, delta: 9, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 8.4, expectedValue: 0, delta: 8.4, variancePercentage: 0, comparisonStatus: "AHEAD" },
            ],
          }
        ],
      },
      {
        startTime: "22:00", endTime: "23:00",
        monitoringEntityIdentitiesComparisonDataList: [
          {
            monitoringEntityIdentity: { processPath: { processId: "1003026", functionId: "1667843456854", processName: "C-Returns Processed", displayName: "Primary Grading" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 13.11, expectedValue: 16.33, delta: -3.23, variancePercentage: -19.77, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 2241, expectedValue: 3495.58, delta: -1254.58, variancePercentage: -35.89, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 171, expectedValue: 214, delta: -43, variancePercentage: -20.09, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 167.46, expectedValue: 214, delta: -46.54, variancePercentage: -21.75, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006653", processName: "C-Returns Support", displayName: "C-Returns Lead/PA" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 13.5, expectedValue: 0.46, delta: 13.04, variancePercentage: 2843.17, comparisonStatus: "AHEAD" },
              { metricName: "VOLUME_PROCESSED", actualValue: 162, expectedValue: 2.75, delta: 159.25, variancePercentage: 5786.34, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 12, expectedValue: 6, delta: 6, variancePercentage: 100, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 11.08, expectedValue: 6, delta: 5.08, variancePercentage: 84.72, comparisonStatus: "AHEAD" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300006654", processName: "C-Returns Support", displayName: "C-Returns Prob Solve" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 5.15, expectedValue: 7.25, delta: -2.09, variancePercentage: -28.9, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 67, expectedValue: 96.95, delta: -29.95, variancePercentage: -30.89, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 13, expectedValue: 14, delta: -1, variancePercentage: -7.14, comparisonStatus: "AT_RISK" },
              { metricName: "HOURS", actualValue: 12.78, expectedValue: 13.38, delta: -0.59, variancePercentage: -4.45, comparisonStatus: "ON_TRACK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "4300016758", processName: "C-Returns Support", displayName: "C-Returns Refurbish" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 8, expectedValue: 10.85, delta: -2.85, variancePercentage: -26.27, comparisonStatus: "CRITICAL" },
              { metricName: "VOLUME_PROCESSED", actualValue: 24, expectedValue: 64.5, delta: -40.5, variancePercentage: -62.79, comparisonStatus: "CRITICAL" },
              { metricName: "HEADCOUNT", actualValue: 3, expectedValue: 6, delta: -3, variancePercentage: -50, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 2.57, expectedValue: 5.94, delta: -3.38, variancePercentage: -56.82, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234561862", processName: "C-Returns Support", displayName: "Team_Mech_Sort" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 2, expectedValue: 2, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HOURS", actualValue: 1.95, expectedValue: 2, delta: -0.05, variancePercentage: -2.47, comparisonStatus: "ON_TRACK" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599235212848", processName: "C-Returns Support", displayName: "C-Returns_Add-Ins" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "VOLUME_PROCESSED", actualValue: 0, expectedValue: 0, delta: 0, variancePercentage: 0, comparisonStatus: "ON_TRACK" },
              { metricName: "HEADCOUNT", actualValue: 0, expectedValue: 6, delta: -6, variancePercentage: -100, comparisonStatus: "CRITICAL" },
              { metricName: "HOURS", actualValue: 0, expectedValue: 5.94, delta: -5.94, variancePercentage: -100, comparisonStatus: "CRITICAL" },
            ],
          },
          {
            monitoringEntityIdentity: { processPath: { processId: "1003058", functionId: "1599234929207", processName: "C-Returns Support", displayName: "Team_Mech_Audit" } },
            monitoringEntityType: "PROCESS_PATH",
            metricsComparisonList: [
              { metricName: "UPH", actualValue: 11.73, expectedValue: 11.14, delta: 0.58, variancePercentage: 5.25, comparisonStatus: "AHEAD" },
              { metricName: "VOLUME_PROCESSED", actualValue: 129, expectedValue: 0, delta: 129, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HEADCOUNT", actualValue: 11, expectedValue: 0, delta: 11, variancePercentage: 0, comparisonStatus: "AHEAD" },
              { metricName: "HOURS", actualValue: 10.18, expectedValue: 0, delta: 10.18, variancePercentage: 0, comparisonStatus: "AHEAD" },
            ],
          }
        ],
      },
    ];

    // Stamp the authoritative DIRECT / INDIRECT classification onto each hourly
    // entity's process path. In production the service supplies `pathType`; the
    // mock literals above omit it, so we derive it here from an INDIRECT set
    // (leadership / add-in / sort support roles) and default everything else to
    // DIRECT. The Intra-Shift trend chart plots DIRECT paths only, so without
    // this the chart would render blank against the mock.
    const INDIRECT_MOCK_PATHS = new Set(['C-Returns Lead/PA', 'C-Returns_Add-Ins', 'Team_Mech_Sort']);
    hourlyShiftComparisonDataList.forEach((window: any) => {
      window.monitoringEntityIdentitiesComparisonDataList.forEach((entity: any) => {
        const path = entity.monitoringEntityIdentity.processPath;
        if (path) {
          path.pathType = INDIRECT_MOCK_PATHS.has(path.displayName) ? 'INDIRECT' : 'DIRECT';
        }
      });
    });

    // Volume target comparisons drive the two summary cards (ALPS Volume Target
    // and Primary Grading Target, with an override) above the table.
    const volumeTargetComparisons = [
      { targetType: 'ALPS_TARGET', targetValue: 30000, actualValue: 23699, delta: -6301, variancePercentage: -21, comparisonStatus: 'CRITICAL' },
      { targetType: 'PRIMARY_GRADING_TARGET', targetValue: 32046, actualValue: 23699, delta: -8347, variancePercentage: -26.05, comparisonStatus: 'CRITICAL' },
      { targetType: 'OVERRIDDEN_PRIMARY_GRADING_TARGET', targetValue: 28000, actualValue: 23699, delta: -4301, variancePercentage: -15.36, comparisonStatus: 'AT_RISK' },
    ];

    // Shift time context drives the shift timeline. Anchor the window to the
    // current day in the viewer's own timezone so local dev sees a live "NOW"
    // marker land inside the window.
    //
    // NOTE: In production the backend sends the timestamps as epoch MILLISECONDS
    // (numbers), e.g. an actual response looks like:
    //   {
    //     "shiftStartTime": 1786716000000,
    //     "shiftEndTime":   1786753800000,
    //     "timezoneId":     "US/Pacific",
    //     "serverTime":     1787071328538
    //   }
    // We keep ISO-8601 strings here (and in tests) for readability; both forms
    // are handled downstream since the timeline/projection helpers pass every
    // value through `new Date(...)`.
    // The hourly comparison windows above run 18:00 → 23:00, so anchor the shift
    // to start at 18:00 and end a few hours after the last data window (03:00 next
    // day) — that leaves 23:00 → 03:00 for the intra-shift chart to show its
    // flat-pace projection past the available data.
    const timezoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const shiftStart = new Date();
    shiftStart.setHours(18, 0, 0, 0);
    const shiftEnd = new Date(shiftStart.getTime() + 9 * 60 * 60 * 1000);
    const shiftTimeContext = {
      shiftStartTime: shiftStart.toISOString(),
      shiftEndTime: shiftEnd.toISOString(),
      timezoneId,
      serverTime: new Date().toISOString(),
    };

    return {
      // Echo back the plan the caller asked for so the report's plan picker can be
      // exercised locally; otherwise report the default resolved baseline.
      compareFlowMonitoringMetadata: {
        baselineReferenceType: params?.baselineShiftPlanId ? 'REQUESTED_SHIFT_PLAN' : 'LATEST_SHIFT_PLAN',
        baselineShiftPlanId: params?.baselineShiftPlanId ?? 'a4fcd063-5921-4560-9ab6-ee54d1f7e542',
      },
      shiftTotalsComparison,
      hourlyShiftComparisonDataList,
      volumeTargetComparisons,
      shiftTimeContext,
      // Latest EOS wash report for this shift plan, surfaced inline on the compare response
      // so local dev exercises the in-shift EOS Wash card.
      eosWashReport: {
        content:
          'Grading fell behind early due to a decant jam (~hour 2); recovered by hour 5 after '
          + 'shifting 6 associates from Support. Watch the AR Grading path next shift.',
        version: 2,
        createdBy: 'jdoe',
        createdAt: Date.now(),
        createdAtIso: new Date().toISOString(),
      },
    };
  }

  static async saveEosWashReport(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    // Echo the optimistic-lock contract: the new version is the caller's baseVersion + 1.
    const baseVersion = Number((params as any)?.baseVersion ?? 0);
    return {
      version: baseVersion + 1,
      createdAt: Date.now(),
    };
  }

  static async getFlowMonitoringData(params?: MockRequestParams) {
    await this.simulateDelay(MOCK_API_DELAY_MS);

    return {
      shiftTotals: [
        {
          monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } },
          monitoringEntityType: 'PROCESS_PATH',
          metricDataList: [
            { metricName: 'UPH', metricValue: 18.66 },
            { metricName: 'VOLUME_PROCESSED', metricValue: 12148.0 },
            { metricName: 'HEADCOUNT', metricValue: 62.0 },
          ],
        },
        {
          monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } },
          monitoringEntityType: 'PROCESS_PATH',
          metricDataList: [
            { metricName: 'UPH', metricValue: 27.36 },
            { metricName: 'VOLUME_PROCESSED', metricValue: 2298.0 },
            { metricName: 'HEADCOUNT', metricValue: 8.0 },
          ],
        },
      ],
      hourlyShiftMonitoringDataList: [
        { startTime: '07:00', endTime: '08:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 12.5 }, { metricName: 'VOLUME_PROCESSED', metricValue: 450.0 }, { metricName: 'HEADCOUNT', metricValue: 36.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 22.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 88.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '08:00', endTime: '09:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 14.2 }, { metricName: 'VOLUME_PROCESSED', metricValue: 710.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 30.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 120.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '09:00', endTime: '10:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 16.8 }, { metricName: 'VOLUME_PROCESSED', metricValue: 840.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 35.5 }, { metricName: 'VOLUME_PROCESSED', metricValue: 142.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '10:00', endTime: '11:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 18.1 }, { metricName: 'VOLUME_PROCESSED', metricValue: 905.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 40.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 160.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '11:00', endTime: '12:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 19.4 }, { metricName: 'VOLUME_PROCESSED', metricValue: 970.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 42.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 168.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        { startTime: '12:00', endTime: '13:00', monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 17.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 850.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 38.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 152.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
        ]},
        {
          startTime: '13:00', endTime: '14:00',
          monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 20.1 }, { metricName: 'VOLUME_PROCESSED', metricValue: 1005.0 }, { metricName: 'HEADCOUNT', metricValue: 50.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 45.0 }, { metricName: 'VOLUME_PROCESSED', metricValue: 180.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
          ],
        },
        {
          startTime: '14:00', endTime: '15:00',
          monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 15.31 }, { metricName: 'VOLUME_PROCESSED', metricValue: 1041.0 }, { metricName: 'HEADCOUNT', metricValue: 68.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 54.75 }, { metricName: 'VOLUME_PROCESSED', metricValue: 219.0 }, { metricName: 'HEADCOUNT', metricValue: 4.0 }] },
          ],
        },
        {
          startTime: '15:00', endTime: '16:00',
          monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 20.39 }, { metricName: 'VOLUME_PROCESSED', metricValue: 1692.0 }, { metricName: 'HEADCOUNT', metricValue: 83.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 37.88 }, { metricName: 'VOLUME_PROCESSED', metricValue: 303.0 }, { metricName: 'HEADCOUNT', metricValue: 8.0 }] },
          ],
        },
        {
          startTime: '16:00', endTime: '17:00',
          monitoringEntityIdentityMetricsDataList: [
            { monitoringEntityIdentity: { processPath: { processId: '1003026', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'C-Returns Processed' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 17.28 }, { metricName: 'VOLUME_PROCESSED', metricValue: 1365.0 }, { metricName: 'HEADCOUNT', metricValue: 79.0 }] },
            { monitoringEntityIdentity: { processPath: { processId: '1002979', functionId: '1667843456854', functionName: 'Primary Grading', displayName: 'AR Grading' } }, monitoringEntityType: 'PROCESS_PATH', metricDataList: [{ metricName: 'UPH', metricValue: 25.4 }, { metricName: 'VOLUME_PROCESSED', metricValue: 254.0 }, { metricName: 'HEADCOUNT', metricValue: 10.0 }] },
          ],
        },
      ],
    };
  }

  /*
   * Mock get shift rotation data
   * Returns a list of associates with rotation priorities and process path assignments
   */
  static async getShiftRoster(params?: MockRequestParams): Promise<GetShiftRosterResponse> {
    await this.simulateDelay();

    const warehouseConfig = this.getWarehouseConfig(params);

    const processPaths = [
      'AFE Pack', 'Inbound Receive', 'SLAM', 'Pick', 'Stow',
      'Decant', 'Problem Solve', 'Rebin', 'Pack Singles', 'Pack Multi',
    ];

    const pickPaths = (count: number): string[] => {
      const shuffled = [...processPaths].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, Math.max(1, Math.min(count, processPaths.length)));
    };

    const employees: ShiftRosterEmployee[] = [
      { alias: 'mgarcia', fullName: 'Garcia, Maria', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'rjohnson', fullName: 'Johnson, Robert', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'swilliams', fullName: 'Williams, Sarah', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
      { alias: 'jbrown', fullName: 'Brown, James', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'pjones', fullName: 'Jones, Patricia', capabilities: pickPaths(4), disallowedPaths: pickPaths(3) },
      { alias: 'mdavis', fullName: 'Davis, Michael', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'jmiller', fullName: 'Miller, Jennifer', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'dwilson', fullName: 'Wilson, David', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'lmoore', fullName: 'Moore, Linda', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
      { alias: 'rtaylor', fullName: 'Taylor, Richard', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'banderson', fullName: 'Anderson, Barbara', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'cthomas', fullName: 'Thomas, Charles', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'sjackson', fullName: 'Jackson, Susan', capabilities: pickPaths(4), disallowedPaths: pickPaths(3) },
      { alias: 'jwhite', fullName: 'White, Joseph', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'jharris', fullName: 'Harris, Jessica', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
      { alias: 'dmartin', fullName: 'Martin, Daniel', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'kthompson', fullName: 'Thompson, Karen', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'mmartinez', fullName: 'Martinez, Mark', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'nrobinson', fullName: 'Robinson, Nancy', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
      { alias: 'sclark', fullName: 'Clark, Steven', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'lrodriguez', fullName: 'Rodriguez, Lisa', capabilities: pickPaths(4), disallowedPaths: pickPaths(3) },
      { alias: 'plewis', fullName: 'Lewis, Paul', capabilities: pickPaths(3), disallowedPaths: pickPaths(1) },
      { alias: 'blee', fullName: 'Lee, Betty', capabilities: pickPaths(4), disallowedPaths: pickPaths(2) },
      { alias: 'awalker', fullName: 'Walker, Andrew', capabilities: pickPaths(2), disallowedPaths: pickPaths(1) },
      { alias: 'dhall', fullName: 'Hall, Dorothy', capabilities: pickPaths(3), disallowedPaths: pickPaths(2) },
    ];

    console.log(`[Mock Data] Returning shift roster data with ${employees.length} employees for ${warehouseConfig.name}`);

    return {
      employees,
      totalCount: employees.length,
      pageNumber: 1,
      totalPages: Math.ceil(employees.length / 15),
      limit: 15,
    };
  }

  /*
   * Mock get planned headcount data
   *
   * Returns the planned headcount per process path for the selected shift, split
   * across the first and second half. Mirrors the prototype `planned-hc` screen:
   * primary paths carry physical lines/stations while support paths do not.
   * This is the successor to getShiftRoster and will eventually replace it.
   */
  static async getPlannedHeadcount(params?: MockRequestParams): Promise<GetPlannedHeadcountResponse> {
    await this.simulateDelay();

    const warehouseConfig = this.getWarehouseConfig(params);

    const primaryPaths: PlannedHeadcountPath[] = [
      { name: 'Clean Decant', functionId: '482910384712', lines: 4, stations: 80, firstHalfHeadcount: 79, secondHalfHeadcount: 75 },
      { name: 'Primary', functionId: '591827463058', lines: 5, stations: 100, firstHalfHeadcount: 65, secondHalfHeadcount: 60 },
      { name: 'FAST', functionId: '374019285634', lines: 2, stations: 40, firstHalfHeadcount: 25, secondHalfHeadcount: 25 },
      { name: 'Apparel/Shoes', functionId: '628174093856', lines: 2, stations: 40, firstHalfHeadcount: 22, secondHalfHeadcount: 20 },
      { name: 'Advanced Grading', functionId: '193847562014', lines: 2, stations: 30, firstHalfHeadcount: 22, secondHalfHeadcount: 22 },
      { name: 'Amazon Resale Tech', functionId: '847261930475', lines: 2, stations: 24, firstHalfHeadcount: 16, secondHalfHeadcount: 14 },
      { name: 'Amazon Resale Non-Tech', functionId: '562039184726', lines: 2, stations: 24, firstHalfHeadcount: 16, secondHalfHeadcount: 14 },
      { name: 'Refurb', functionId: '918273645091', lines: 1, stations: 10, firstHalfHeadcount: 6, secondHalfHeadcount: 6 },
      { name: 'Eurosort', functionId: '304958172639', lines: 2, stations: 16, firstHalfHeadcount: 8, secondHalfHeadcount: 8 },
    ];

    const supportPaths: PlannedHeadcountPath[] = [
      { name: 'Audit', functionId: '729384016258', firstHalfHeadcount: 15, secondHalfHeadcount: 12 },
      { name: 'Problem Solve', functionId: '485920173648', firstHalfHeadcount: 8, secondHalfHeadcount: 8 },
      { name: 'Advanced Detection', functionId: '610293847562', firstHalfHeadcount: 12, secondHalfHeadcount: 10 },
      { name: 'Waterspider', functionId: '837461029385', firstHalfHeadcount: 15, secondHalfHeadcount: 12 },
      { name: 'Unloaders', functionId: '294817360592', firstHalfHeadcount: 45, secondHalfHeadcount: 40 },
    ];

    console.log(`[Mock Data] Returning planned headcount with ${primaryPaths.length} primary and ${supportPaths.length} support paths for ${warehouseConfig.name}`);

    return {
      planStatus: PlanStatus.PENDING_APPROVAL,
      primaryPaths,
      supportPaths,
    };
  }

  /*
   * Mock get assignments data
   * Returns a GetStationAssignmentsResponse with ~30 associates distributed across
   * 4 process paths (Primary, Clean decant, Resale, Problem solve). The
   * returned response satisfies the location-exclusivity invariant: every
   * associate in `response.associates` appears in at most one workstation
   * or indirect-path list.
   */
  static async getStationAssignment(params?: MockRequestParams): Promise<GetStationAssignmentsResponse> {
    await this.simulateDelay();

    const warehouseConfig = this.getWarehouseConfig(params);

    const availableTrainedPaths = [
      'Primary',
      'Clean decant',
      'Resale',
      'Problem solve',
      'Pick',
      'Stow',
    ];

    // Deterministic trained-paths picker so the mock is stable across calls.
    const pickTrainedPaths = (seed: number, count: number): string[] => {
      const paths: string[] = [];
      for (let i = 0; i < count; i += 1) {
        paths.push(availableTrainedPaths[(seed + i) % availableTrainedPaths.length]);
      }
      return paths;
    };

    // Roster of 30 associates. First 24 are clocked in; last 6 are not
    // clocked in (and will also be unassigned). 5 carry warnings. Each carries
    // a realistic, distinct login alias (the primary display label on the
    // redesigned screen) so local dev renders alias-based labels the way prod
    // does, rather than a single placeholder alias for everyone.
    const warningIds = new Set(['A005', 'A011', 'A019', 'A023', 'A027']);
    // Reusable sample decisions for local dev. USER_OVERRIDE carries a reason
    // code + optional free text; ALGORITHM carries a backend reason code.
    const userOverride: AssignmentDecision = {
      source: 'USER_OVERRIDE',
      reasonCode: 'LABOR_SHARE',
      decisionReason: 'Test',
    };
    const algorithmNoQuota: AssignmentDecision = {
      source: 'ALGORITHM',
      reasonCode: 'NO_QUOTA',
    };
    const algorithmRotation: AssignmentDecision = {
      source: 'ALGORITHM',
      reasonCode: 'LEFT_RIGHT_ROTATION',
    };
    const userOverrideOps: AssignmentDecision = {
      source: 'USER_OVERRIDE',
      reasonCode: 'OPERATIONAL_NEED',
      decisionReason: 'Covering absence',
    };

    // Each roster entry may carry a sample `decision` so the info (ℹ️) popup
    // has data to show in local dev. A mix of USER_OVERRIDE / ALGORITHM is
    // seeded; entries with no `decision` still exercise the "No decision
    // recorded" state.
    const roster: Array<{ name: string; alias: string; decision?: AssignmentDecision }> = [
      { name: 'Maria Garcia', alias: 'mgarci', decision: userOverride },        // A001
      { name: 'Robert Johnson', alias: 'rjohnso', decision: algorithmNoQuota }, // A002
      { name: 'Sarah Williams', alias: 'swillia', decision: algorithmRotation },// A003
      { name: 'James Brown', alias: 'jbrownn', decision: userOverrideOps },     // A004
      { name: 'Patricia Jones', alias: 'pjoness', decision: algorithmNoQuota }, // A005
      { name: 'Michael Davis', alias: 'micdavi' },                             // A006
      { name: 'Jennifer Miller', alias: 'jenmill', decision: userOverride },    // A007
      { name: 'David Wilson', alias: 'dwilso', decision: algorithmNoQuota },    // A008
      { name: 'Linda Moore', alias: 'lmooree' },                               // A009
      { name: 'Richard Taylor', alias: 'rtaylo', decision: algorithmRotation },// A010
      { name: 'Barbara Anderson', alias: 'bander', decision: userOverrideOps }, // A011
      { name: 'Charles Thomas', alias: 'cthomas', decision: algorithmNoQuota }, // A012
      { name: 'Susan Jackson', alias: 'sjackso' },                             // A013
      { name: 'Joseph White', alias: 'jwhitee', decision: userOverride },       // A014
      { name: 'Jessica Harris', alias: 'jharris', decision: algorithmNoQuota }, // A015
      { name: 'Daniel Martin', alias: 'danmart', decision: algorithmRotation }, // A016
      { name: 'Karen Thompson', alias: 'kthomps' },                            // A017
      { name: 'Mark Martinez', alias: 'mmartin', decision: algorithmNoQuota },  // A018
      { name: 'Nancy Robinson', alias: 'nrobins', decision: userOverrideOps },  // A019
      { name: 'Steven Clark', alias: 'sclarkk', decision: algorithmNoQuota },   // A020
      { name: 'Lisa Rodriguez', alias: 'lrodrig' },                            // A021
      { name: 'Paul Lewis', alias: 'plewiss', decision: algorithmNoQuota },     // A022
      { name: 'Betty Lee', alias: 'bleee', decision: userOverride },            // A023
      { name: 'Andrew Walker', alias: 'awalke', decision: algorithmRotation },  // A024
      { name: 'Dorothy Hall', alias: 'dhalll' },                               // A025
      { name: 'Kevin Allen', alias: 'kallenn' },                               // A026
      { name: 'Sandra Young', alias: 'syoung' },                               // A027
      { name: 'Brian King', alias: 'bkingg' },                                 // A028
      { name: 'Donna Wright', alias: 'dwrigh' },                               // A029
      { name: 'Edward Scott', alias: 'escottt' },                              // A030
    ];

    // ── Extra roster for exercising the Staffing board's auto-scroll ──
    //
    // The Associate View / Staffing board paginates letter-grouped cards to
    // fit the viewport and auto-rotates between pages. A 30-person roster fits
    // on a single page on most monitors, so it never rotates. To make the
    // auto-scroll visible in local dev, append a large, alphabetically diverse
    // set of associates spanning A–Z. These carry ids A031+ and are NOT
    // referenced by any workstation/indirect assignment below, so they flow
    // through as UNASSIGNED associates — enough letter groups to overflow into
    // several pages that the board then cycles through. (In the test/dev
    // environment these cards intentionally read alias + "Unassigned"; that is
    // acceptable, and keeping them unassigned is what makes the multi-page
    // auto-scroll visible.)
    //
    // Aliases are derived from the last name (first 5 letters, lowercased) plus
    // a disambiguating suffix, matching the login-style label the board sorts
    // and groups by. Kept deterministic so the mock is stable across reloads.
    const extraLastNames = [
      'Abbott', 'Acosta', 'Bautista', 'Bishop', 'Cabrera', 'Chan', 'Doyle',
      'Duran', 'Ellison', 'Estrada', 'Farrell', 'Fuentes', 'Gallagher',
      'Gibson', 'Hampton', 'Hoffman', 'Ingram', 'Iverson', 'Jennings',
      'Joseph', 'Keller', 'Knight', 'Lambert', 'Livingston', 'Maddox',
      'Mercado', 'Norton', 'Nunez', 'Oconnor', 'Odonnell', 'Pacheco',
      'Pittman', 'Quinn', 'Quintana', 'Rasmussen', 'Rowe', 'Salinas',
      'Sweeney', 'Tapia', 'Trevino', 'Underwood', 'Valdez', 'Vaughn',
      'Wagner', 'Whitaker', 'Xiong', 'Yates', 'Yoder', 'Zamora', 'Zuniga',
    ];
    const extraFirstNames = [
      'Alex', 'Bianca', 'Caleb', 'Diana', 'Elena', 'Felix', 'Grace',
      'Henry', 'Iris', 'Jorge', 'Kira', 'Liam', 'Mona', 'Noah', 'Ola',
      'Priya', 'Quinn', 'Rosa', 'Sam', 'Tara', 'Uri', 'Vera', 'Wes',
      'Xena', 'Yusuf', 'Zoe',
    ];
    extraLastNames.forEach((last, i) => {
      const first = extraFirstNames[i % extraFirstNames.length];
      // 5-letter last-name stem + a letter suffix keeps aliases unique while
      // preserving the last name's first letter for grouping.
      const alias =
        last.slice(0, 5).toLowerCase() + String.fromCharCode(97 + (i % 26));
      roster.push({ name: `${first} ${last}`, alias });
    });

    // Build the canonical associates list. `assignedWorkstationId` is set
    // below once each associate's target location has been decided.
    // A026 (Kevin Allen / kallenn) is seeded with a long, explicit list of 10
    // trained paths so local dev exercises the modal's truncation + "full list
    // on hover" tooltip. Everyone else gets 2-4 deterministic paths.
    const longTrainedPaths = [
      'Primary',
      'Clean decant',
      'Resale',
      'Problem solve',
      'Pick',
      'Stow',
      'Water Spider',
      'Advanced Detection',
      'WD Grading',
      'C-Returns Prob Solve',
    ];

    const associates: AssignmentAssociate[] = roster.map(({ name, alias, decision }, idx) => {
      const seq = idx + 1;
      const associateId = `A${String(seq).padStart(3, '0')}`;
      // 2-4 trained paths, varied deterministically.
      const trainedPathCount = 2 + (idx % 3); // 2, 3, or 4
      return {
        associateId,
        associateName: name,
        associateAlias: alias,
        // First 24 of the original roster are clocked in; keep the appended
        // associates clocked in too so they render as active staffed cards.
        isClocked: seq <= 24 || seq > 30,
        hasWarning: warningIds.has(associateId),
        trainedPaths: associateId === 'A026' ? longTrainedPaths : pickTrainedPaths(idx, trainedPathCount),
        ...(decision ? { decision } : {}),
      };
    });

    // Helper: fetch an associate by id from the canonical list. Throws if
    // the id is missing, which keeps the mock-construction logic honest.
    const lookup = (associateId: string): AssignmentAssociate => {
      const found = associates.find(a => a.associateId === associateId);
      if (!found) {
        throw new Error(`Mock roster is missing associate ${associateId}`);
      }
      return found;
    };

    // Helper: mark an associate as assigned to a given workstation. The
    // `assignedWorkstationId` convenience marker on the canonical entry is
    // kept in sync. Returns a shallow copy for insertion into the
    // workstation's `assignedAssociates` list.
    // The real API tags each assigned associate with a per-station code
    // (e.g. "ws-02-01-R") that is distinct from the line-level workstationId
    // ("Line 2"). Mirror that here so local dev exercises the same shape the
    // redesigned screen renders in its "Show WS IDs" view.
    const stationCode = (line: number, slot: number): string =>
      `ws-${String(line).padStart(2, '0')}-${String(slot).padStart(2, '0')}-${line % 2 === 0 ? 'R' : 'L'}`;

    const assignTo = (
      associateId: string,
      workstationId: string,
      assignedWorkstationId?: string,
    ): AssignmentAssociate => {
      const canonical = lookup(associateId);
      canonical.assignedWorkstationId = assignedWorkstationId ?? workstationId;
      return { ...canonical };
    };

    // An empty (planned-but-unfilled) slot. Mirrors the real API, which returns
    // a placeholder entry per unfilled planned position: no `associateId`
    // (so `isEmptySlot` is true), other fields defaulted, and — for workstation
    // slots — an `assignedWorkstationId`.
    const emptySlot = (assignedWorkstationId?: string): AssignmentAssociate => ({
      associateName: '',
      isClocked: false,
      hasWarning: false,
      trainedPaths: [],
      assignedWorkstationId,
    });

    // The mock mirrors the three process-path shapes returned by the real
    // GetStationAssignment API:
    //
    //   1. DIRECT + PROCESS_LINES  → multiple "Line N" workstations; rendered
    //                                as line-level workstation cards.
    //   2. DIRECT + DEPARTMENT     → a single function-named workstation whose
    //                                associates render as a horizontal list.
    //   3. INDIRECT + PROCESS_LINES → no workstation slots; associates live in
    //                                `nonWorkstationAssociates` and render
    //                                horizontally.

    // (1) Primary Grading: 3 lines, 3 associates each (A001–A009).
    const primaryLineAssignments: string[][] = [
      ['A001', 'A002', 'A003'], // Line 1
      ['A004', 'A005', 'A006'], // Line 2
      ['A007', 'A008', 'A009'], // Line 3
    ];
    const primaryWorkstations: Workstation[] = primaryLineAssignments.map((ids, i) => {
      const workstationId = `Line ${i + 1}`;
      return {
        workstationId,
        workstationName: `Line ${i + 1}`,
        capacity: 3,
        assignedAssociates: ids.map((id, slot) =>
          assignTo(id, workstationId, stationCode(i + 1, slot + 1)),
        ),
      };
    });

    // (1b) WD Grading: a second DIRECT + PROCESS_LINES function so the
    // redesigned screen shows more than one line-based path. 2 lines, 3
    // associates each (A020–A025), drawn from the previously-unassigned pool.
    const wdLineAssignments: string[][] = [
      ['A020', 'A021', 'A022'], // Line 1
      ['A023', 'A024', 'A025'], // Line 2
    ];
    const wdWorkstations: Workstation[] = wdLineAssignments.map((ids, i) => {
      const workstationId = `WD Line ${i + 1}`;
      const line = i + 4;
      // Offset the station-code line number (4, 5) so these codes stay distinct
      // from Primary Grading's lines in the "Show WS IDs" view.
      const real = ids.map((id, slot) =>
        assignTo(id, workstationId, stationCode(line, slot + 1)),
      );
      // Two planned-but-unfilled positions per line → empty placeholder slots
      // (3 assigned of 5 planned), exercising the dashed "Add associate" slot.
      const empties = [real.length, real.length + 1].map((slot) =>
        emptySlot(stationCode(line, slot + 1)),
      );
      return {
        workstationId,
        workstationName: `Line ${i + 1}`,
        capacity: 5,
        assignedAssociates: [...real, ...empties],
      };
    });

    // (2) C-Returns Refurbish (DEPARTMENT): single function-named workstation,
    // 3 assigned associates (A010–A012) of 5 planned — the two unfilled
    // positions come back as empty slots, rendered as dashed "Add associate"
    // placeholders in the pool.
    const refurbishWorkstationId = '4300016758';
    const refurbishWorkstations: Workstation[] = [
      {
        workstationId: refurbishWorkstationId,
        workstationName: 'C-Returns Refurbish',
        capacity: 5,
        assignedAssociates: [
          ...['A010', 'A011', 'A012'].map((id, slot) =>
            assignTo(id, refurbishWorkstationId, stationCode(1, slot + 1)),
          ),
          emptySlot(stationCode(1, 4)),
          emptySlot(stationCode(1, 5)),
        ],
      },
    ];

    // (2) C-Returns Prob Solve (DEPARTMENT): single function-named workstation,
    // 2 associates (A013–A014).
    const probSolveWorkstationId = '4300006654';
    const probSolveWorkstations: Workstation[] = [
      {
        workstationId: probSolveWorkstationId,
        workstationName: 'C-Returns Prob Solve',
        capacity: 5,
        assignedAssociates: ['A013', 'A014'].map((id, slot) =>
          assignTo(id, probSolveWorkstationId, stationCode(2, slot + 1))
        ),
      },
    ];

    // (3) Indirect functions: no workstation slots — associates live in
    // `nonWorkstationAssociates`. Each list mixes filled entries with empty
    // (planned-but-unfilled) placeholders — entries with no id/name/alias,
    // matching the real API's INDIRECT `nonWorkstationAssociates` shape — so
    // the redesigned screen shows the inline "Add associate" typeahead on
    // non-workstation pool slots (2 assigned + 2 empty = 4 planned each).
    const mezzNonWorkstationAssociates: AssignmentAssociate[] = [
      ...['A015', 'A016', 'A017'].map(id => ({ ...lookup(id) })),
      emptySlot(),
      emptySlot(),
    ];
    const waterSpiderNonWorkstationAssociates: AssignmentAssociate[] = [
      ...['A018', 'A019'].map(id => ({ ...lookup(id) })),
      emptySlot(),
      emptySlot(),
    ];

    const processPaths: ProcessPath[] = [
      {
        fclmFunctionId: '1667843456854',
        processPathName: 'Primary Grading',
        pathType: PathType.DIRECT,
        staffingMode: StaffingMode.PROCESS_LINES,
        metrics: {
          totalFlow: 1200,
          actual: 9,
          assigned: 9,
          delta: 0,
          avgRatePerHour: 150,
        },
        workstations: primaryWorkstations,
        departmentName: 'Customer Returns',
        processPathMappingName: 'C-Returns Processed',
        fclmProcessPathId: 1003026,
      },
      {
        fclmFunctionId: '1667843456855',
        processPathName: 'WD Grading',
        pathType: PathType.DIRECT,
        staffingMode: StaffingMode.PROCESS_LINES,
        metrics: {
          totalFlow: 800,
          actual: 6,
          assigned: 6,
          delta: 0,
          avgRatePerHour: 120,
        },
        workstations: wdWorkstations,
        departmentName: 'Customer Returns',
        processPathMappingName: 'WD Processed',
        fclmProcessPathId: 1003027,
      },
      {
        fclmFunctionId: '4300016758',
        processPathName: 'C-Returns Refurbish',
        pathType: PathType.DIRECT,
        staffingMode: StaffingMode.DEPARTMENT,
        metrics: {
          actual: 3,
          assigned: 3,
          delta: 0,
          dr: 0.5,
          avgScanRate: 30,
          unitsPerHour: 40,
          processedPackages: 120,
        },
        workstations: refurbishWorkstations,
        nonWorkstationAssociates: [],
        departmentName: 'Customer Returns',
        processPathMappingName: 'C-Returns Support',
        fclmProcessPathId: 1003058,
      },
      {
        fclmFunctionId: '4300006654',
        processPathName: 'C-Returns Prob Solve',
        pathType: PathType.DIRECT,
        staffingMode: StaffingMode.DEPARTMENT,
        metrics: {
          actual: 2,
          assigned: 2,
          delta: 0,
          dr: 0.4,
          avgScanRate: 28,
          unitsPerHour: 35,
          processedPackages: 90,
        },
        workstations: probSolveWorkstations,
        nonWorkstationAssociates: [],
        departmentName: 'Customer Returns',
        processPathMappingName: 'C-Returns Support',
        fclmProcessPathId: 1003058,
      },
      {
        fclmFunctionId: '1599234750696',
        processPathName: 'C-Returns_Mezz',
        pathType: PathType.INDIRECT,
        staffingMode: StaffingMode.PROCESS_LINES,
        metrics: {
          totalFlow: 0,
          actual: 3,
          assigned: 3,
          delta: 0,
        },
        workstations: [],
        nonWorkstationAssociates: mezzNonWorkstationAssociates,
        departmentName: 'Customer Returns',
        processPathMappingName: 'C-Returns Support',
        fclmProcessPathId: 1003058,
      },
      {
        fclmFunctionId: '4300006861',
        processPathName: 'Water Spider',
        pathType: PathType.INDIRECT,
        staffingMode: StaffingMode.PROCESS_LINES,
        metrics: {
          totalFlow: 0,
          actual: 2,
          assigned: 2,
          delta: 0,
        },
        workstations: [],
        nonWorkstationAssociates: waterSpiderNonWorkstationAssociates,
        departmentName: 'Customer Returns',
        processPathMappingName: 'C-Returns Support',
        fclmProcessPathId: 1003058,
      },
    ];

    // The top-level `associates` array in the real API only contains
    // UNASSIGNED associates (those not placed in any workstation). Associates
    // that are assigned exist solely within processPaths[].workstations[].assignedAssociates
    // or processPaths[].nonWorkstationAssociates (for department-style functions).
    const assignedIds = new Set<string>();
    for (const path of processPaths) {
      for (const ws of path.workstations) {
        for (const a of ws.assignedAssociates) {
          // Skip empty (planned-but-unfilled) slots — they carry no id.
          if (a.associateId) assignedIds.add(a.associateId);
        }
      }
      for (const a of path.nonWorkstationAssociates ?? []) {
        if (a.associateId) assignedIds.add(a.associateId);
      }
    }
    const unassignedAssociates = associates.filter(a => !assignedIds.has(a.associateId ?? ''));

    // Top-level metrics derived from the actual roster/assignment distribution.
    //   isClocked: false → 6 (A025–A030); everyone else is clocked in.
    //   assigned: 9 Primary Grading + 6 WD Grading + 3 Refurbish
    //             + 2 Prob Solve + 3 Mezz + 2 Water Spider = 25 (A001–A025).
    //   The appended A031+ roster (Staffing-board auto-scroll fixture) is
    //   clocked in but unassigned, so it inflates the unassigned pool.
    // Derive the clocked counts from the list so they stay correct as the
    // roster grows, rather than hardcoding a headcount.
    const clockedInCount = associates.filter(a => a.isClocked).length;
    const notClockedInCount = associates.length - clockedInCount;
    const metrics: AssignmentsMetrics = {
      totalUnlocked: notClockedInCount,
      totalClockedIn: clockedInCount,
      totalActual: associates.length,
      // Real-API shape. The legacy per-category counts below are retained only
      // for the pre-redesign OldMetricsBar path (removed with the old screen).
      assignedProcessPaths: [],
      assignedPrimary: 9,
      assignedProblemSolve: 2,
      assignedResale: 0,
      assignedCleanDecant: 0,
      assignedIndirect: 5,
    };

    console.log(
      `[Mock Data] Returning assignments data with ${unassignedAssociates.length} unassigned associates across ${processPaths.length} process paths for ${warehouseConfig.name}`
    );

    // PA/Lead/HR pool fixtures (backend jobLevel >= 2, excluded from workstation
    // assignment). Distinct ids so they never collide with the A0xx roster, and
    // intentionally NOT present in `associates` or any process path so the
    // location-exclusivity invariant holds. Each carries a PA_LEAD_HR_EXCLUDED
    // ALGORITHM decision so the info (ℹ️) popup has data to show.
    const paLeadHRExcluded: AssignmentDecision = {
      source: 'ALGORITHM',
      reasonCode: 'PA_LEAD_HR_EXCLUDED',
    };
    // 12 PA/Lead/HR associates so the box can be verified with a high count
    // (the list scrolls internally at max-height: 60vh). Ids PA001+ never
    // collide with the A0xx roster; a rotating trainedPaths pick keeps entries
    // varied. A couple carry warnings to exercise the warning affordance.
    const paLeadHRRoster: Array<{ name: string; alias: string }> = [
      { name: 'Olivia Martinez', alias: 'olmarti' },
      { name: 'Daniel Kim', alias: 'dankim' },
      { name: 'Grace Okafor', alias: 'gokafor' },
      { name: 'Marcus Bell', alias: 'marbell' },
      { name: 'Priya Nair', alias: 'priynai' },
      { name: 'Tomás Rivera', alias: 'tomrive' },
      { name: 'Hannah Schmidt', alias: 'hanschm' },
      { name: 'Wei Chen', alias: 'weichen' },
      { name: 'Fatima Al-Sayed', alias: 'fatalsa' },
      { name: 'Derek Johnson', alias: 'derjohn' },
      { name: 'Sofia Romano', alias: 'sofroma' },
      { name: 'Isaac Weber', alias: 'isawebe' },
    ];
    const unassignedPALeadHRAssociates: AssignmentAssociate[] = paLeadHRRoster.map(
      (entry, i) => ({
        associateId: `PA${String(i + 1).padStart(3, '0')}`,
        associateName: entry.name,
        associateAlias: entry.alias,
        isClocked: i % 4 !== 0, // every 4th not clocked in
        hasWarning: i % 5 === 0, // every 5th carries a warning
        trainedPaths: [availableTrainedPaths[i % availableTrainedPaths.length]],
        decision: paLeadHRExcluded,
      }),
    );

    return {
      firstHalfStationAssignment: {
        associates: unassignedAssociates,
        unassignedPALeadHRAssociates,
        processPaths,
        metrics,
      },
      secondHalfStationAssignment: buildSecondHalf(),
      lastModifiedBy: 'moultn@',
      lastModifiedDate: '01/14/2026 10:35 AM',
    };

    // Build a VISIBLY DIFFERENT second half so switching the half dropdown is
    // observable in local dev. The real API returns genuinely different data
    // per half; here we derive a smaller variant from the same structures so
    // the difference is obvious (fewer process paths, and a couple of
    // associates moved out of workstations into the unassigned pool) while the
    // location-exclusivity invariant still holds (an associate is either in a
    // workstation OR in the unassigned pool, never both).
    function buildSecondHalf(): StationAssignmentHalfData {
      // Take only the first three process paths for the second half so the
      // board looks different from the first half's full set.
      const secondHalfPaths = processPaths.slice(0, 3).map((path) => ({
        ...path,
        // Drop the last assigned associate from each workstation (leave empty
        // slots and any single-slot workstations untouched) so headcounts
        // differ visibly between halves.
        workstations: path.workstations.map((ws) => {
          const realAssigned = ws.assignedAssociates.filter((a) => !isEmptySlot(a));
          if (realAssigned.length <= 1) return ws;
          const dropped = realAssigned[realAssigned.length - 1];
          return {
            ...ws,
            assignedAssociates: ws.assignedAssociates.filter(
              (a) => a.associateId !== dropped.associateId,
            ),
          };
        }),
      }));

      // Recompute which ids remain assigned in the trimmed second half, then
      // treat everyone else from the roster as unassigned for this half.
      const secondHalfAssignedIds = new Set<string>();
      for (const path of secondHalfPaths) {
        for (const ws of path.workstations) {
          for (const a of ws.assignedAssociates) {
            if (a.associateId) secondHalfAssignedIds.add(a.associateId);
          }
        }
        for (const a of path.nonWorkstationAssociates ?? []) {
          if (a.associateId) secondHalfAssignedIds.add(a.associateId);
        }
      }
      const secondHalfUnassigned = associates.filter(
        (a) => a.associateId && !secondHalfAssignedIds.has(a.associateId),
      );

      return {
        associates: secondHalfUnassigned,
        unassignedPALeadHRAssociates,
        processPaths: secondHalfPaths,
        metrics,
      };
    }
  }

  static async queryAgent(params?: MockRequestParams): Promise<{ answer: string; nodeId: string }> {
    const nodeId = params?.nodeId || 'TUS1';
    const question = (params?.question || '').toLowerCase();

    await new Promise(resolve => setTimeout(resolve, 1200));

    if (question.includes('rate') || question.includes('grading')) {
      return {
        answer: `## Primary Grading Rate — ${nodeId} (10:00–11:00)\n\n| Process Path | Function | UPH | Volume | HC |\n|---|---|---:|---:|---:|\n| C-Returns Processed | Primary Grading | **35.8** | **2,542** | **71** |\n| C-Returns Processed | Primary Grading | **36.9** | **2,657** | **72** |\n\nYour plan target is **38.6 UPH**, so you're at **95.5% to plan**. The gap is ~2.7 UPH below target.\n\n> **Tip:** Check if any associates have high inferred time — that's often the biggest controllable factor.\n\n_Source: FCLM | Shift Plan_`,
        nodeId,
      };
    }

    if (question.includes('water spider') || question.includes('ws')) {
      return {
        answer: `## Water Spider Staffing — ${nodeId}\n\nBased on the standard staffing ratio for **Water Spiders**:\n\n- **Sort sites**: 1 WS per 21 direct associates (Full Case) or 1:14 (Team/Mech)\n- **CALM Code**: \`CRSDCNTF\`\n\nWith your current **71 Primary Grading** associates:\n→ Recommended: **71 ÷ 21 = 4 Water Spiders** (Full Case)\n\nYour shift plan shows **9 Water Spiders** currently staffed — above standard ratio. Additional WS may cover stow sweep or transport.\n\n_Source: Operations KB | Shift Plan_`,
        nodeId,
      };
    }

    if (question.includes('plan') || question.includes('on plan')) {
      return {
        answer: `## Plan Comparison — ${nodeId} Day Shift\n\n| Function | Plan Rate | Actual Rate | % to Plan |\n|---|---|---:|---:|\n| Primary Grading | 38.6 | 36.2 | **93.8%** |\n| WHD Grading | 24.0 | 22.8 | **95.0%** |\n| Transfer Out | 1,058 | 1,131 | **106.9%** |\n\nOverall you're slightly below plan on grading functions. Transfer Out is exceeding target.\n\n> **Recommendation:** Rebalance 1-2 associates from Transfer Out to Primary Grading to close the gap.\n\n_Source: Shift Plan | FCLM_`,
        nodeId,
      };
    }

    if (question.includes('override')) {
      return {
        answer: `## Active Overrides — ${nodeId} Day Shift\n\n| Override | Original | Current | Set By |\n|---|---|---|---|\n| Primary Grading HC | 74 | **71** | moultn@ |\n| WS Ratio | 1:21 | **1:14** | ratheer@ |\n\nThese overrides were applied during shift plan review. The WS ratio override explains why you have 9 Water Spiders instead of the standard 4.\n\n_Source: Shift Plan (SPMS)_`,
        nodeId,
      };
    }

    if (question.includes('buffer') || question.includes('wip')) {
      return {
        answer: `## Buffer Status — ${nodeId}\n\n| Buffer | Current | Target | Status |\n|---|---:|---:|---|\n| Pre-Grade | **2,847** | 3,000 | On track |\n| Post-Grade | **1,203** | 1,500 | Below target |\n| WHD | **456** | 400 | Slightly over |\n\nPost-Grade buffer is running low. At current rates, it will be depleted in approximately **45 minutes** without inbound replenishment.\n\n> **Tip:** Monitor inbound trailer ETA — next trailer expected at 15:15.\n\n_Source: FCLM_`,
        nodeId,
      };
    }

    return {
      answer: `I can help you with information about **${nodeId}**. Try asking about:\n\n- Current rates and UPH\n- Staffing ratios (water spiders, problem solve)\n- Plan comparison ("Am I on plan?")\n- Active overrides\n- Buffer/WIP status\n- Volume projections\n\nWhat would you like to know?\n\n_Source: Operations KB_`,
      nodeId,
    };
  }

  /**
   * Get mock leaderboard data for local development
   * Matches the GetLeaderboard API response structure from RnRRCCFacadeService
   */
  static async getLeaderboard(params?: MockRequestParams): Promise<LeaderboardData> {
    await this.simulateDelay();
    return {
      weekOf: "2026-06-23",
      generatedAt: "2026-06-29T17:39:00Z",
      badges: [
        {
          badgeId: "topSites",
          frequency: "weekly",
          entries: [
            { site: "IAH1", rank: 1, planCount: 10, daysActive: 7, dayPlans: 2, nightPlans: 8 },
            { site: "HOU3", rank: 2, planCount: 8, daysActive: 5, dayPlans: 7, nightPlans: 1 },
            { site: "TUS1", rank: 3, planCount: 3, daysActive: 3, dayPlans: 1, nightPlans: 2 },
            { site: "CHA1", rank: 4, planCount: 1, daysActive: 1, dayPlans: 1, nightPlans: 0 },
            { site: "LAS2", rank: 4, planCount: 1, daysActive: 1, dayPlans: 0, nightPlans: 1 },
          ],
        },
        {
          badgeId: "firstTimeAdopter",
          frequency: "one-time",
          entries: [
            { site: "PHX7", firstPlanDate: "2026-06-25" },
            { site: "SEA4", firstPlanDate: "2026-06-27" },
          ],
        },
        {
          badgeId: "consistentSites",
          frequency: "monthly",
          entries: [
            { site: "IAH1", daysActive: 28, dayPlans: 28, nightPlans: 28 },
            { site: "HOU3", daysActive: 28, dayPlans: 28, nightPlans: 28 },
          ],
        },
      ],
    };
  }

  static async queryShiftPlanAgent(params?: MockRequestParams): Promise<{ answer: string; nodeId: string; planId: string }> {
    const nodeId = params?.nodeId || 'TUS1';
    const planId = params?.planId || '';
    const date = params?.date || params?.ofdDate || '';
    const question = (params?.question || '').toLowerCase();

    // Simulate Bedrock latency (~2s for local dev, real P50 is ~12s)
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Handle missing plan context
    if (!planId || !date) {
      return {
        answer: `I need a few more details to look up that plan. Please provide:\n\n- **nodeId** (warehouse code)\n- **date** (plan date)\n- **planId** (the plan ID)\n\nYou can navigate to a shift plan in the Shift Planning page and I'll automatically pick up the context.`,
        nodeId,
        planId,
      };
    }

    if (question.includes('headcount') || question.includes('people') || question.includes('why')) {
      return {
        answer: `## Primary Grading Headcount — ${nodeId} (${date})\n\n**214 associates** are allocated to Primary Grading at 07:00 because:\n\n1. **Forecasted volume**: 8,142 units expected between 07:00–08:00\n2. **Target UPH**: 38.0 (from historical performance at ${nodeId})\n3. **Calculation**: 8,142 ÷ 38.0 = **214.3** → rounded to **214**\n\n> The algorithm uses a 4-week rolling average of actual UPH for the same day-of-week and shift.\n\n| Factor | Value | Source |\n|---|---|---|\n| Forecast volume | 8,142 | Volume forecast model |\n| Target UPH | 38.0 | 4-week rolling avg |\n| Computed HC | 214.3 | volume ÷ UPH |\n| Final HC | **214** | Rounded down |\n\n_Source: Decision Log | Plan ${planId}_`,
        nodeId,
        planId,
      };
    }

    if (question.includes('rate') || question.includes('uph')) {
      return {
        answer: `## Rate Assumptions — ${nodeId} (${date})\n\nThe shift plan uses these target rates:\n\n| Function | Target UPH | Source | Lookback |\n|---|---:|---|---|\n| Primary Grading | **38.0** | 4-week rolling avg | Same DOW, same shift |\n| WHD Grading | **24.2** | 4-week rolling avg | Same DOW, same shift |\n| Stow | **45.6** | Station config | Manual override |\n\n> Rates are computed from the **4 most recent matching shifts** (same day-of-week, same shift type). Outlier shifts (>2 std dev) are excluded.\n\n_Source: Algorithm Reference Docs_`,
        nodeId,
        planId,
      };
    }

    if (question.includes('volume') || question.includes('forecast')) {
      return {
        answer: `## Volume Forecast — ${nodeId} (${date})\n\nThe plan uses these hourly volume projections:\n\n| Hour | Forecast Units | Confidence |\n|---|---:|---|\n| 07:00 | **8,142** | High (±5%) |\n| 08:00 | **7,891** | High (±5%) |\n| 09:00 | **6,234** | Medium (±12%) |\n| 10:00 | **5,876** | Medium (±12%) |\n\nTotal shift volume: **42,567 units**\n\n> Forecast is sourced from the ML volume prediction model, updated daily at 04:00.\n\n_Source: Volume Forecast Model | Plan ${planId}_`,
        nodeId,
        planId,
      };
    }

    return {
      answer: `I can explain the shift plan for **${nodeId}** on **${date}** (Plan: ${planId}).\n\nTry asking about:\n\n- **Headcount**: "Why are there X people in Primary Grading at 07:00?"\n- **Rates**: "What UPH target was used for Clean Decant?"\n- **Caps**: "Was any function capped? What would it have been without the cap?"\n- **Volume**: "What volume forecast drove this hour's staffing?"\n\nWhat would you like to understand?\n\n_Source: Shift Plan Explainability Agent_`,
      nodeId,
      planId,
    };
  }

  /**
   * Mock implementation of isValidStationAssignmentOverride.
   * Randomly returns either a rotation risk warning or no errors.
   */
  static async isValidStationAssignmentOverride(params?: MockRequestParams): Promise<IsValidStationAssignmentOverrideResponse> {
    await this.simulateDelay();

    const shouldWarn = Math.random() > 0.5;

    if (shouldWarn) {
      return {
        validationErrors: [
          'This associate is at a Rotation risk (left/right) at this designation',
        ],
      };
    }

    return {
      validationErrors: [],
    };
  }

  /**
   * Mock implementation of getSiteLayoutConfig.
   * Returns a realistic saved site layout with 2-3 process paths using
   * a mix of PROCESS_LINES and DEPARTMENT staffing modes.
   */
  static async getSiteLayoutConfig(params?: MockRequestParams): Promise<GetSiteLayoutConfigResponse> {
    await this.simulateDelay();

    return {
      siteLayout: {
        savedProcessPaths: [
          {
            processId: 'PP-001',
            processPathName: 'Resale',
            departmentName: 'C-Returns Processed',
            functionId: 'FN-001',
            functionName: 'Customer Returns',
            functionType: 'DIRECT',
            staffingMode: 'PROCESS_LINES',
            lines: [
              {
                lineId: 'line-resale-A',
                lineName: 'Line 01',
                lineCapacity: 12,
                workstations: [
                  { id: 'ws-001', workstationId: 'RS-A-001', enabled: true },
                  { id: 'ws-002', workstationId: 'RS-A-002', enabled: true },
                  { id: 'ws-003', workstationId: 'RS-A-003', enabled: true },
                  { id: 'ws-004', workstationId: 'RS-A-004', enabled: true },
                  { id: 'ws-005', workstationId: 'RS-A-005', enabled: true },
                ],
              },
              {
                lineId: 'line-resale-B',
                lineName: 'Line 02',
                lineCapacity: 10,
                workstations: [
                  { id: 'ws-006', workstationId: 'RS-B-001', enabled: true },
                  { id: 'ws-007', workstationId: 'RS-B-002', enabled: true },
                  { id: 'ws-008', workstationId: 'RS-B-003', enabled: true },
                ],
              },
            ],
            individualWorkstations: [],
          },
          {
            processId: 'PP-002',
            processPathName: 'Primary Grading',
            departmentName: 'AR Grading',
            functionId: 'FN-002',
            functionName: 'Primary Grading',
            functionType: 'DIRECT',
            staffingMode: 'PROCESS_LINES',
            lines: [
              {
                lineId: 'line-pg-1',
                lineName: 'Line 01',
                lineCapacity: 20,
                workstations: [
                  { id: 'ws-009', workstationId: 'PG-1-001', enabled: true },
                  { id: 'ws-010', workstationId: 'PG-1-002', enabled: true },
                  { id: 'ws-011', workstationId: 'PG-1-003', enabled: true },
                  { id: 'ws-012', workstationId: 'PG-1-004', enabled: true },
                  { id: 'ws-013', workstationId: 'PG-1-005', enabled: true },
                  { id: 'ws-014', workstationId: 'PG-1-006', enabled: true },
                ],
              },
            ],
            individualWorkstations: [],
          },
          {
            processId: 'PP-003',
            processPathName: 'Problem Solve',
            departmentName: 'C-Returns Support',
            functionId: 'FN-003',
            functionName: 'C-Returns Prob Solve',
            functionType: 'INDIRECT',
            staffingMode: 'DEPARTMENT',
            lines: [],
            individualWorkstations: [
              { id: 'ws-015', workstationId: 'PS-001', enabled: true },
              { id: 'ws-016', workstationId: 'PS-002', enabled: true },
              { id: 'ws-017', workstationId: 'PS-003', enabled: true },
              { id: 'ws-018', workstationId: 'PS-004', enabled: true },
            ],
          },
        ],
      },
      lastModifiedBy: 'moultn@',
      lastModifiedAt: '2025-01-15T14:32:00Z',
    };
  }

  /**
   * Mock implementation of getAvailableProcessPaths.
   * Returns at least 5 process path configs with a mix of DIRECT and INDIRECT function types.
   */
  static async getAvailableProcessPaths(params?: MockRequestParams): Promise<GetAvailableProcessPathsResponse> {
    await this.simulateDelay();

    return {
      processPathConfigs: [
        {
          processId: 'PP-001',
          processPathName: 'Resale',
          departmentName: 'C-Returns Processed',
          functionId: 'FN-001',
          functionName: 'Customer Returns',
          functionType: 'DIRECT',
        },
        {
          processId: 'PP-002',
          processPathName: 'Primary Grading',
          departmentName: 'AR Grading',
          functionId: 'FN-002',
          functionName: 'Primary Grading',
          functionType: 'DIRECT',
        },
        {
          processId: 'PP-003',
          processPathName: 'Problem Solve',
          departmentName: 'C-Returns Support',
          functionId: 'FN-003',
          functionName: 'C-Returns Prob Solve',
          functionType: 'INDIRECT',
        },
        {
          processId: 'PP-004',
          processPathName: 'Clean Decant',
          departmentName: 'C-Returns Clean Decant Processed',
          functionId: 'FN-004',
          functionName: 'Clean Decant',
          functionType: 'DIRECT',
        },
        {
          processId: 'PP-005',
          processPathName: 'Audit',
          departmentName: 'C-Returns Support',
          functionId: 'FN-005',
          functionName: 'C-Return Audit',
          functionType: 'INDIRECT',
        },
        {
          processId: 'PP-006',
          processPathName: 'WHD Grading',
          departmentName: 'AR Speciality Grading',
          functionId: 'FN-006',
          functionName: 'WD Grading - AR',
          functionType: 'DIRECT',
        },
      ],
    };
  }

  /**
   * Mock implementation of saveSiteLayoutConfig.
   * Accepts the save payload and returns mock counts with a timestamp.
   */
  static async saveSiteLayoutConfig(params?: MockRequestParams): Promise<SaveSiteLayoutConfigResponse> {
    await this.simulateDelay(400);

    const processPaths = params?.processPaths || [];
    const pathCount = Array.isArray(processPaths) ? processPaths.length : 0;

    return {
      savedAt: new Date().toISOString(),
      addedCount: Math.max(1, pathCount),
      updatedCount: Math.max(0, pathCount - 1),
      deletedCount: 0,
    };
  }

  // ─── Certificate Rules (in-memory, per warehouse) ────────────────────────────

  private static readonly CERT_RULES_CONFLICT_MESSAGE = 'This rule was changed by someone else. Reload and try again.';

  private static readonly CERT_RULES_FUNCTIONS: MappableFunction[] = [
    { processId: '1003058', functionId: '4300006654', functionName: 'C-Returns Prob Solve', processPath: 'C-Returns Support', department: 'Customer Returns' },
    { processId: '1003041', functionId: '4300006861', functionName: 'Water Spider', processPath: 'C-Returns Support', department: 'Customer Returns' },
    { processId: '1003045', functionId: '4300006861', functionName: 'Water Spider', processPath: 'C-Returns Processed', department: 'Customer Returns' },
    { processId: '1003030', functionId: '4300006700', functionName: 'Refurbish', processPath: 'Refurb', department: 'Customer Returns' },
    { processId: '1003050', functionId: '4300006902', functionName: 'Apparel / Shoes', processPath: 'Resale', department: 'Customer Returns' },
  ];

  private static certRulesStore: Record<string, CertificateFunctionMapping[]> = {};

  private static certRulesFor(warehouseId: string): CertificateFunctionMapping[] {
    if (!this.certRulesStore[warehouseId]) {
      const f = this.CERT_RULES_FUNCTIONS;
      const seed = (fn: MappableFunction, expression: object): CertificateFunctionMapping => ({
        ...fn,
        expression: JSON.stringify(expression),
        version: 1,
        lastModifiedBy: 'seed',
        lastModifiedAt: '2026-09-29T00:00:00Z',
      });
      this.certRulesStore[warehouseId] = [
        // OR: A OR B
        seed(f[0], { type: 'OR', children: [
          { type: 'HAS', cert: '5b070f10-67c3-5f42-b34f-96d9c270822e' },
          { type: 'HAS', cert: '9d2c41e7-3a8b-4f1c-a6e2-1b7f0c93d5a4' },
        ] }),
        // AND: A AND B
        seed(f[3], { type: 'AND', children: [
          { type: 'HAS', cert: 'c3e8a915-07d2-4b6f-8e1a-52f9d4b07c61' },
          { type: 'HAS', cert: 'e71b0d42-9c5a-4e83-b2f6-0a4d8c1e39b7' },
        ] }),
        // Mixed: A OR (B AND C)
        seed(f[1], { type: 'OR', children: [
          { type: 'HAS', cert: '2f6a9c13-d84e-4b07-91c5-7e3b0a6d2f48' },
          { type: 'AND', children: [
            { type: 'HAS', cert: '5b070f10-67c3-5f42-b34f-96d9c270822e' },
            { type: 'HAS', cert: 'a84d1e6c-3b9f-4c20-8d57-f1e2a09b6c35' },
          ] },
        ] }),
      ];
    }
    return this.certRulesStore[warehouseId];
  }

  /**
   * Mock implementation of getCertificateFunctionMappings.
   * Returns the in-memory mappings for the warehouse plus 5 mappable functions.
   */
  static async getCertificateFunctionMappings(params?: MockRequestParams): Promise<GetCertificateFunctionMappingsResponse> {
    await this.simulateDelay();
    const warehouseId = params?.warehouseId || 'TUS1';
    return {
      mappings: this.certRulesFor(warehouseId).map((m) => ({ ...m })),
      availableFunctions: this.CERT_RULES_FUNCTIONS.map((f) => ({ ...f })),
      rulesEnabled: false,
    };
  }

  /**
   * Mock implementation of upsertCertificateFunctionMapping.
   * Create when version is omitted, update when it matches; any mismatch throws the conflict message.
   */
  static async upsertCertificateFunctionMapping(params?: MockRequestParams): Promise<UpsertCertificateFunctionMappingResponse> {
    await this.simulateDelay(400);
    const warehouseId = params?.warehouseId || 'TUS1';
    const rules = this.certRulesFor(warehouseId);
    const index = rules.findIndex((m) => m.processId === params?.processId && m.functionId === params?.functionId);
    const current = index >= 0 ? rules[index] : undefined;
    const version = params?.version ?? undefined;
    if ((current && version !== current.version) || (!current && version !== undefined)) {
      throw new Error(this.CERT_RULES_CONFLICT_MESSAGE);
    }
    const fn = this.CERT_RULES_FUNCTIONS.find((f) => f.processId === params?.processId && f.functionId === params?.functionId);
    if (!fn) {
      throw new Error(`Function ${params?.processId}|${params?.functionId} is not configured for warehouse ${warehouseId}`);
    }
    if (current && current.expression === params?.expression) {
      return { mapping: { ...current } };
    }
    const saved: CertificateFunctionMapping = {
      ...fn,
      expression: params?.expression,
      version: current ? current.version + 1 : 1,
      lastModifiedBy: params?.requestedBy,
      lastModifiedAt: new Date().toISOString(),
    };
    if (index >= 0) rules[index] = saved;
    else rules.push(saved);
    return { mapping: { ...saved } };
  }

  /**
   * Mock implementation of deleteCertificateFunctionMapping.
   */
  static async deleteCertificateFunctionMapping(params?: MockRequestParams): Promise<DeleteCertificateFunctionMappingResponse> {
    await this.simulateDelay(400);
    const warehouseId = params?.warehouseId || 'TUS1';
    const rules = this.certRulesFor(warehouseId);
    const index = rules.findIndex((m) => m.processId === params?.processId && m.functionId === params?.functionId);
    if (index < 0) throw new Error('Mapping not found');
    if (rules[index].version !== params?.version) throw new Error(this.CERT_RULES_CONFLICT_MESSAGE);
    rules.splice(index, 1);
    return { deleted: true };
  }

  /**
   * Get mock DryRunShiftPlan response for the Checkout page.
   * Simulates the per-process-path headcount preview from the algorithm.
   * When outputAdjustment is provided, applies percentage adjustments to headcounts.
   */
  static async dryRunShiftPlan(params?: MockRequestParams): Promise<DryRunShiftPlanResponse> {
    await this.simulateDelay(800);

    const baseEntries: DryRunProcessPathEntry[] = [
      {
        functionName: 'Primary Grading',
        functionId: 'fclm-pg-001',
        type: ProcessPathFunctionType.DIRECT,
        headcount: 105,
        maxStaffing: 120,
        hours: 1050,
        hoursPer1000Units: 23.33,
      },
      {
        functionName: 'Clean Decant',
        functionId: 'fclm-cd-002',
        type: ProcessPathFunctionType.DIRECT,
        headcount: 18,
        maxStaffing: 25,
        hours: 180,
        hoursPer1000Units: 4.0,
      },
      {
        functionName: 'WD Grading',
        functionId: 'fclm-wdg-003',
        type: ProcessPathFunctionType.DIRECT,
        headcount: 14,
        hours: 140,
        hoursPer1000Units: 3.11,
      },
      {
        functionName: 'Water Spider',
        functionId: 'fclm-ws-004',
        type: ProcessPathFunctionType.INDIRECT,
        headcount: 40,
        maxStaffing: 50,
        hours: 400,
      },
      {
        functionName: 'WD Training',
        functionId: 'fclm-wdt-005',
        type: ProcessPathFunctionType.INDIRECT,
        headcount: 2,
        hours: 20,
      },
      {
        functionName: 'WD Ambassador',
        functionId: 'fclm-wda-006',
        type: ProcessPathFunctionType.INDIRECT,
        headcount: 1,
        maxStaffing: 2,
        hours: 10,
      },
    ];

    const outputAdjustment: OutputAdjustmentEntry[] | null = params?.outputAdjustment || null;

    // Apply adjustments if provided
    const processPathEntries = baseEntries.map((entry) => {
      if (!outputAdjustment) return entry;

      const adj = outputAdjustment.find((a: OutputAdjustmentEntry) => a.functionName === entry.functionName);
      if (!adj) return entry;

      // If disabled, exclude from output (headcount = 0)
      if (adj.disabled) {
        return { ...entry, headcount: 0, hours: 0 };
      }

      // Apply percentage adjustment (0-200 scale, 100 = no change)
      let adjustedHC = Math.round(entry.headcount * adj.overridePercentage / 100);

      // Clamp to maxStaffing
      if (entry.maxStaffing && adjustedHC > entry.maxStaffing) {
        adjustedHC = entry.maxStaffing;
      }
      if (adjustedHC < 0) adjustedHC = 0;

      const hours = adjustedHC * 10; // 10 hours per shift
      return { ...entry, headcount: adjustedHC, hours };
    }).filter((entry) => entry.headcount > 0); // Remove disabled paths

    const baselineTotalHC = baseEntries.reduce((sum, e) => sum + e.headcount, 0);
    const totalHC = processPathEntries.reduce((sum, e) => sum + e.headcount, 0);
    const directHours = processPathEntries
      .filter(e => e.type === ProcessPathFunctionType.DIRECT)
      .reduce((sum, e) => sum + (e.hours || 0), 0);
    const indirectHours = processPathEntries
      .filter(e => e.type === ProcessPathFunctionType.INDIRECT)
      .reduce((sum, e) => sum + (e.hours || 0), 0);

    return {
      planId: params?.planId || 'plan-mock-001',
      availableHeadcount: baselineTotalHC,
      tunedHeadcount: outputAdjustment ? totalHC : undefined,
      totalHours: directHours + indirectHours,
      processPath: processPathEntries,
    };
  }
}
